import React from 'react';
import { createI18n, type MessageValue, type Messages } from '@weretradeit/hooks';

export const messages: Messages = {
  de: {
    nav: {
      home: 'Home',
      about: 'Team',
      shop: 'Shop',
      stats: 'Stats',
      login: 'Login',
      weretradeIT: 'weretradeIT',
      bauKlotzBude: 'BauKlotzBude',
      imprint: 'Impressum',
      privacy: 'Datenschutz',
    },
    platform: {
      badge: 'Plattform • Deep Dive',
      title: 'Unsere Plattform & interne Apps',
      lead:
        'Ein Blick hinter die Kulissen: Serverless-Stack auf Cloudflare mit Single Sign-on, gemeinsamen UI-Komponenten und zentralen Services für Metriken, Logging und E-Mail.',
      archTitle: 'Architektur',
      archKicker: 'Cloudflare Native',
      toolsTitle: 'Kern-Apps',
      toolsKicker: 'Tools',
      uiTitle: 'UI & Screenshots',
      uiKicker: 'Design',
      contactTitle: 'Kontakt & Demos',
      contactKicker: 'Partner',
      coreApps: {
        title: 'Kern-Apps & Commerce',
        kicker: 'Production',
        points: [
          { title: 'ShopBeaver', body: 'Modernes React/Vite Storefront.' },
          { title: 'InventarOwl', body: 'Zentraler Inventory-Sync und Lager-Worker.' },
          { title: 'AccountingFlare', body: 'Finanz- & Reports-Hub (PayPal/eBay/BrickLink), D1 + R2.' },
          { title: 'Internal Dashboard', body: 'KPI-Monitor, Service-Links, Versionsübersicht.' },
          { title: 'Services Portal', body: 'Docs, Logging, Vault, Brief Library – alles auf einer Oberfläche.' }
        ]
      },
      infrastructureHubs: {
        title: 'Multi-Node Mesh Hubs',
        kicker: 'Infrastructure',
        points: [
          { title: 'lair404', body: 'Business-Primärknoten (Docker, Grafana, Vault, Honeypot).' },
          { title: 'n1njanode', body: 'Private/Family Knoten (RAG-API, Mail-Zero, MCP Server).' },
          { title: 't3chnodrome', body: 'Private Guard Node zum Schutz des n1njanode-Ökosystems.' },
          { title: 'd1mension503', body: 'High-Availability Guard Post Ökosystem.' },
          { title: 'h0menode', body: 'Lokales Caddy Mesh Gateway & Cloudflare SSO.' }
        ]
      },
      aiAgentLayer: {
        title: 'KI & Agenten-Schicht',
        kicker: 'Intelligence',
        points: [
          { title: 'LiteLLM Proxy', body: 'Unified Gateway mit Logical Model Pools für 100+ Integrationen.' },
          { title: 'Dynamic RAG Memory', body: 'Vektorbasierte Dokumentensuche für verteiltes Wissen.' },
          { title: 'Supervisors', body: 'weretrade-supervisor & scheugenpflug-supervisor für Monitoring.' },
          { title: 'BKB Agents', body: 'Automatisierte Fulfillment-Agenten mit eBay & Slack Integration.' },
          { title: 'MCP Servers', body: '{mcpServers} spezialisierte Context-Server.' }
        ]
      },
      managedForks: {
        title: 'Managed Forks',
        kicker: 'Open Source Tools',
        points: [
          { title: 'dbgate-fork', body: 'Angepasste Datenbank-Management-Lösung.' },
          { title: 'scanopy-fork', body: 'Netzwerktopologie-Visualisierung.' },
          { title: 'vscode-server-fork', body: 'VS Code im Browser via Cloudflare Access.' },
          { title: 'n8n-fork', body: 'OIDC Auth und Multi-Server Automatisierung.' },
          { title: 'nextcloud-fork', body: 'Nextcloud Cluster (Server, Desktop, Passwords) für h0menode.' }
        ]
      },
      uiCopy:
        'Wir erweitern die UI-Galerie schrittweise. Das Design bleibt hell/dunkel-fähig und nutzt das gemeinsame Kit.',
      uiPlaceholderSub: 'Interne UI-Einblicke werden hier ergänzt.',
      contactCopy:
        'Interesse an Kooperation, White-Labeling oder Einblick in einzelne Module? Wir richten gern Demo-Zugänge ein.',
    },
    cleaning: {
      badge: 'Reinigung • Deep Dive',
      title: 'So reinigen wir LEGO-Steine',
      lead:
        'Hinter den Kulissen: unser mehrstufiger Prozess für hygienische, farbstabile Bricks – optimiert für AFOL-Ansprüche.',
      processTitle: 'Prozessübersicht',
      processKicker: 'Workflow',
      whyTitle: 'Warum das zählt',
      whyKicker: 'Qualität',
      nextTitle: 'Nächste Schritte',
      nextKicker: 'Service',
      contactTitle: 'Kontakt & Aufträge',
      contactKicker: 'Fragen?',
      processSteps: [
        { title: 'Wareneingang & Sortierung', body: 'Sichtprüfung, Trennung nach Farben/Material, Entfernung von Fremdteilen.' },
        { title: 'Ultraschall-Reinigung', body: 'Feinste Vibrationen lösen Schmutz und Fett ohne Druck oder Hitze.' },
        { title: 'Gefilterte Waschlösungen', body: 'pH-mild, kunststofffreundlich, rückstandsarm; kein aggressives Chemie-Bad.' },
        { title: 'Klarspülen & Trocknung', body: 'Gefilterte Luft, schonende Temperatur; separierte Chargen verhindern Abrieb.' },
        { title: 'Finish & Qualitätscheck', body: 'Manuelle Stichproben, Klemmkraft-Check und staubfreie Verpackung.' },
      ],
      whyPoints: [
        { title: 'Volle Klemmkraft', body: 'Saubere Pins/Noppen bedeuten stabile MOCs.' },
        { title: 'Keine Mikrokratzer', body: 'Sanfte Medien & keine harten Bürsten.' },
        { title: 'Farbstabilität', body: 'Getrennte Bäder nach Farbe/Material verhindern Verfärbungen.' },
        { title: 'Hygiene', body: 'Staub, Nikotin, Öl – alles draußen, bevor die Teile zu dir kommen.' },
      ],
      nextCopy:
        'Wir bauen gerade eine bebilderte Galerie unserer Reinigungsstrecke. Sobald die Fotos fertig sind, landen sie hier.',
      bannerPill: 'Screenshot (Platzhalter)',
      bannerSub: 'Reinigungsanlage & Ultraschall in Aktion – Coming Soon.',
      contactCopy:
        'Du willst größere Mengen reinigen lassen oder hast Fragen zum Prozess? Melde dich gern:',
    },
    stats: {
      title: 'Stat Surrender',
      subtitle: 'Öffentliche, reduzierte Kennzahlen',
      updated: 'Aktualisiert',
      overview: 'Überblick',
      ytd: 'Jahr (YTD)',
      d30: 'Letzte 30 Tage',
      eur: 'EUR Umsatz',
      orders: 'Bestellungen',
      items: 'Artikel verkauft',
      shipping: 'Versand (EUR)',
      distOrders: 'Verteilung Bestellungen',
      distRevenue: 'Verteilung Umsatz',
      stockTitle: 'Gesamt-Bestand',
      totalItems: 'Total Items',
      totalLots: 'Total Lots',
      totalValue: 'Gesamtwert',
      sourceBL: 'BrickLink',
      sourceEB: 'eBay',
      visitors: 'Besucher',
      pageviews: 'Seitenaufrufe',
      uniqueVisitors: 'Eindeutige Besucher',
      trafficSources: 'Traffic-Quellen',
      direct: 'Direkt',
      referral: 'Verweise',
      search: 'Suchmaschinen',
      social: 'Soziale Medien',
      platformSplit: 'Verteilung nach Plattform',
      domain: 'Domain',
      footer: '© {{year}} BauKlotzBude · Öffentliche Kennzahlen',
    },
    order: {
      kicker: 'Bestellung',
      title: 'Status {{id}}',
      loading: 'Lade Bestellung…',
      error: 'Bestellung nicht gefunden oder Fehler beim Laden.',
      status: 'Status',
      total: 'Gesamt',
      totalSub: 'inkl. Versand',
      subtotal: 'Zwischensumme',
      subtotalSub: 'exkl. Versand',
      shipping: 'Versand',
      shippingSub: 'geschätzt',
      items: 'Artikel',
      noItems: 'Keine Artikel gespeichert.',
    },
    login: {
      login: 'Login',
      register: 'Registrieren',
      forgot: 'Passwort vergessen',
      reset: 'Passwort zurücksetzen',
      loginLead: 'Melde dich mit deinen Zugangsdaten an.',
      registerLead: 'Neues Konto anlegen (nur freigegebene E-Mail-Adressen).',
      forgotLead: 'Wir senden dir einen Link zum Zurücksetzen.',
      resetLead: 'Neues Passwort setzen.',
      email: 'E-Mail',
      password: 'Passwort',
      passwordConfirm: 'Passwort bestätigen',
      submitLogin: 'Login',
      submitRegister: 'Registrieren',
      submitForgot: 'Link senden',
      submitReset: 'Passwort setzen',
      backToLogin: 'Zurück zum Login',
      alreadyAccount: 'Schon Account? Login',
      newHere: 'Neu hier? Registrieren',
      forgotLink: 'Passwort vergessen?',
      allowlistNote: 'E-Mails werden gegen die Allowlist geprüft.',
      mismatch: 'Passwörter stimmen nicht überein.',
      forgotMsg: 'Wenn ein Konto existiert, haben wir dir einen Link gesendet.',
      resetMsg: 'Passwort gesetzt. Du kannst dich jetzt einloggen.',
      noToken: 'Kein Reset-Token gefunden. Bitte nutze den Link aus der E-Mail.',
      footer: 'Login, Registrierung, Passwort vergessen und Reset laufen hier zusammen.',
      actionFailed: 'Aktion fehlgeschlagen',
    },
    landing: {
      heroBadge: 'BrickCommerce & IT',
      heroTitle: 'Einfach. Klar. Direkt.',
      heroLead:
        'Offizielle Landing Page von weretrade IT UG (haftungsbeschränkt) – zentrale Anlaufstelle für unsere Shops, IT-Services und Kontaktwege.',
      // Zahlen als {platzhalter}: die Seite reicht die Live-Metriken nach, damit
      // DE und EN nicht auseinanderlaufen. Siehe data/claims.ts.
      capsule:
        'weretrade IT UG ist ein deutsches Technologieunternehmen, das BauKlotzBude (gebrauchte LEGO-Steine auf BrickLink, eBay, BrickOwl und im eigenen Shop) sowie {apps} KI-gestützte Web-Anwendungen auf Cloudflare-Infrastruktur betreibt. Die Plattform verbindet E-Commerce mit systematischer Fehlervermeidung durch gelernte Muster und serverloser Edge-Architektur für maximale Kosteneffizienz.',
      fact1Term: 'Marktplätze',
      fact1Detail: 'BrickLink, eBay, BrickOwl und eigener Shop — aus einem Lagerbestand',
      fact2Term: 'Plattform',
      fact2Detail: '{apps} Produktions-Apps und {workers} Backend-Services auf Cloudflare',
      fact3Term: 'Entwicklung',
      fact3Detail: 'KI-gestützt mit Claude Code',
      fact4Term: 'Sitz',
      fact4Detail: 'Beratzhausen, Deutschland',
      capsuleCta: 'Mehr über weretrade IT erfahren',
      ctas: {
        bricklink: 'Shop',
        ebay: 'Shop',
        shop: '🛍 BauKlotzBude Shop',
        intranet: '🔐 WebOS / Login',
        stats: '📈 Public Stats',
        google: '📍 Google Firmenprofil',
      },
      amazonPill: 'Amazon Partner',
      amazonPlaceholder: 'Hero Banner 1200×200 (Platzhalter)',
      amazonNote: 'Aktiviere Marketing-Cookies, um den Amazon-Hero (Spielzeug/LEGO) zu sehen.',
      amazonDisclosure: 'Als Amazon-Partner verdienen wir an qualifizierten Verkäufen.',
      shopsKicker: 'Shops',
      shopsTitle: 'Unsere Marktplätze',
      servicesKicker: 'Services',
      servicesTitle: 'IT & interne Tools',
      servicesCopy:
        'Wir entwickeln interne Services (Logging, Credentials Vault, Brief Library) auf Cloudflare. Für Kooperationen oder Auftragsarbeit sprich uns an.',
      servicesList: ['Serverless Edge-APIs', 'React Frontends & Design System', 'Automatisierung & Datenpipelines'],
      servicesCta: 'Öffentliche Kennzahlen ansehen',
      servicesKiCta: 'KI & Automatisierung entdecken',
      showcasesTitle: 'SaaS App Showcases',
      showcaseAccountingFox: 'accountingFox (GoBD & DATEV)',
      showcaseInventarOwl: 'inventarOwl + marketShark',
      showcaseInfrastructureDog: 'infrastructureDog (Full Control)',
      contactKicker: 'Kontakt',
      contactTitle: 'So erreichst du uns',
      contactPref: 'Bevorzugt über BrickLink-Nachricht. Alternativ per E-Mail.',
      contactAddress: 'Adresse: Eichenstraße 14, 93176 Beratzhausen, Deutschland',
      socialKicker: 'Social',
      socialTitle: 'Aktuelle Posts',
      socialHighlight: 'Highlight-Post',
      socialNewest: 'Neuester Post',
      socialPlaceholder: 'Bald zeigen wir hier einen ausgewählten Social-Media-Post (Instagram/Pinterest/Facebook).',
      socialPlaceholder2: 'Wird automatisch mit dem jüngsten Social-Update befüllt, sobald der Feed angebunden ist.',
      codeKicker: 'Code Metrics',
      codeTitle: 'Repo Kennzahlen',
      codeLead: 'Automatisch gemessen: Gesamtzeilen, Zeichen und Dateien des Monorepos.',
      codeStatusWarn: 'Live-Daten derzeit nicht abrufbar – gezeigt werden Schätzwerte.',
      advKicker: 'Advertiser Snapshot',
      advTitle: 'Reichweite & Qualität',
      advLead: 'Kombinierte Sichtbarkeit aus BrickLink + eBay. Ideal für Partner- oder Kampagnenflächen.',
      advWarn: 'Live-APIs nicht erreichbar · zeige Schätzwerte',
      advPositives: 'Positive Ratings',
      advVisits: 'Monatliche Visits',
      advCtr: 'CTR Kampagnen',
      advConversion: 'Conversion',
      advNote: 'Live-Ratings stammen direkt aus unseren Marktplatz-APIs; Traffic/CTR sind konservative Benchmarks aus den letzten Kampagnen.',
      platformKicker: 'Plattform',
      platformTitle: 'Unsere internen Apps',
      platformCopy:
        'Unser Stack läuft komplett serverless auf Cloudflare. Wir nutzen ein gemeinsames Design-System, Single Sign-on und zentrale Services wie Logging, Metriken und E-Mail.',
      platformList: [
        'Internal Dashboard: KPI-Monitoring, Service-Links und Team-Shortcuts.',
        'AccountingFlare: Finanz- und Report-Hub mit D1 + R2, BrickLink/eBay/PayPal-Auswertungen.',
        'Services Portal: Docs, Logging, Brief Library, Credentials Vault – alles auf einer Oberfläche.',
        'Inventar Owl: Lager-Worker + Pages-Frontend für Bestände und Dateien.',
        'Code Metrics & Feedback: Öffentliche Metriken, anonymisierte Bewertungen und Status-Badges.',
      ],
      platformScreenshotPill: 'Screenshots (Platzhalter)',
      platformScreenshotSub: 'Interne UI-Einblicke landen hier – Login/SSO geschützt.',
      platformNote: 'Zugriff auf die internen Apps erfolgt über das Frontdoor-Login. Für Demos oder Partnerzugänge sprich uns an.',
      readMore: 'Weiterlesen',
      cleaningKicker: 'Reinigung',
      cleaningTitle: 'So reinigen wir LEGO-Steine',
      cleaningCopy:
        'Saubere Bricks sind die Basis für zufriedene AFOLs. Wir setzen auf moderne Verfahren, die schonend und gründlich arbeiten – von Ultraschallbädern für Kleinteile bis zu separaten Trocknungsläufen mit gefilterter Luft.',
      cleaningList: [
        'Ultraschall: Feinste Vibrationen lösen Staub und Fett, ohne Druck oder Hitze.',
        'Gefilterte Waschlösungen: pH-mild, kunststofffreundlich, rückstandsarm.',
        'Getrennte Chargen: Sortiert nach Farbe/Material, um Abrieb und Verfärbung zu vermeiden.',
        'Trocknung & Finish: Luftgefilterte Trocknung, abschließende Sichtprüfung und Verpackung.',
      ],
      cleaningScreenshotPill: 'Screenshot (Platzhalter)',
      cleaningScreenshotSub: 'Bild der Reinigungsstrecke folgt – wird nachgereicht.',
      cleaningResult: 'Ergebnis: Hygienisch saubere Steine, volle Klemmkraft, keine Mikrokratzer – bereit für dein nächstes MOC.',
      feedbackKicker: 'Feedback',
      feedbackTitle: 'Kundenzufriedenheit',
      feedbackLead: 'Auszüge aus BrickLink & eBay (anonymisiert).',
      feedbackLabel: 'Positive Bewertungen',
      adsensePill: 'Google AdSense',
      adsenseTitle: '',
      adsenseNote: 'Aktiviere Marketing-Cookies, um personalisierte Werbung zu sehen.',
      footerMade: 'made with ❤️ weretradeIT © {{year}}',
      footerSocials: 'Soziale Kanäle',
    },
    wt: {
      badge: 'Brand',
      title: 'weretradeIT',
      lead: 'weretradeIT steht für moderne IT-Lösungen, serverless Architektur und innovative Entwicklung. Hier findest du alle Artikel und Updates rund um unsere Technologie-Plattform.',
      about: {
        title: 'Über weretradeIT',
        kicker: 'About',
        copy: 'weretradeIT ist die IT-Marke der weretrade IT UG (haftungsbeschränkt). Wir bauen Produktionssoftware mit KI-gestützter Entwicklung statt mit großen Teams: {apps} Apps und {workers} Services laufen vollständig serverless auf Cloudflare Workers, Pages, D1 und R2 — kein Server, den nachts jemand patchen muss. Dieselbe Arbeitsweise bringen wir in E-Commerce-Plattformen, interne Tools und Prozessautomatisierung unserer Kunden ein. Was dieses Modell kostet und was dabei herauskommt, zeigen die beiden Widgets weiter unten — mit offengelegter Rechnung.',
      },
      tech: {
        title: 'Built With',
        kicker: 'Technologies',
        copy: 'Unsere Plattform nutzt modernste Technologien für Performance, Skalierbarkeit und Developer Experience.',
      },
      posts: {
        title: 'Blog Posts & Artikel',
        kicker: 'Content',
        lead: 'Alle Artikel, Tutorials und Einblicke rund um weretradeIT und unsere Technologie-Plattform.',
        empty: 'Keine Artikel vorhanden. Schau bald wieder vorbei!',
        plattform: {
          title: 'Unsere Plattform & interne Apps',
          desc: 'Ein tiefer Einblick in unsere Cloudflare-basierte Architektur, Shared UI, Auth-System und interne Tools.',
        },
        entwicklung: {
          title: 'Selbstlernende Entwicklung',
          desc: 'Wie wir ein System entwickelt haben, das sich selbst verbessert. Systematische Fehlervermeidung durch gelernte Muster.',
        },
        ki: {
          title: 'KI, Anbindungen & Automatisierung',
          desc: 'KI-Workflows, Marktplatz-Anbindungen und Automatisierungen für Händler — gebaut und betrieben von weretradeIT.',
        },
        zuruf: {
          title: 'Ansicht auf Zuruf: Eine Frage. Eine Ansicht.',
        },
      },
      contact: {
        title: 'Kontakt & Services',
        kicker: 'Get in Touch',
        copy: 'Interesse an unseren IT-Services, White-Labeling oder Kooperationen? Wir entwickeln auch kundenspezifische Lösungen.',
      },
      roadmap: {
        title: 'Forschungsphasen 2025–2027+',
        kicker: 'Vision',
        lead: 'Unsere Forschungsreise: Vom experimentellen Start im Mai 2025 zur datengetriebenen Plattform.',
        start: {
          date: 'Mai 2025',
          title: 'Gründung',
          desc: 'weretrade IT UG gegründet. Start der Entwicklung selbstlernender Systeme.',
        },
        q1_2026: {
          date: 'Q1 2026',
          title: 'Public Self-Improving Services',
          desc: 'Release generischer, selbstlernender Services: Ask anything, get better output from day to day.',
        },
        phase_2027: {
          date: '2027+',
          title: 'Forschungsphase 2',
          desc: 'Erweiterte Datensammlung, Algorithmus-Evaluation, Kooperationen mit Forschungspartnern für Langzeitstudien.',
        },
      },
      blogTitle: 'Artikel',
      blogKicker: 'Beiträge',
      noPosts: 'Weitere Artikel folgen in Kürze.',
      orchestration: {
        badge: 'weretradeIT • Technologie',
        title: 'Warum Orchestrierung Fine-Tuning schlägt',
        desc: 'Ein smarterer Ansatz für KI: Statt Millionen in eigene Modelle zu investieren, haben wir eine intelligente Orchestrierungsschicht entwickelt.',
      },
      ki: {
        badge: 'KI-Workflows und Anbindungen',
        title: 'KI und Automatisierung, die im Betrieb bleibt.',
        lead: 'Wir bauen und betreiben KI-Workflows, Marktplatz-Anbindungen und Automatisierungen für mittelständische Händler. Betrieb auf eigener Infrastruktur, Abrechnung nach Aufwand.',
        ctaPrimary: 'Erstgespräch vereinbaren',
        ctaSecondary: 'Mehr über weretradeIT',
        cardsTitle: 'Unsere Schwerpunkte',
        cards: {
          ai: {
            title: 'KI-Workflows',
            body: 'Orchestrierung statt Fine-Tuning: Multi-Provider-Gateway mit automatischen Fallbacks (Claude, GPT, Llama, Gemini), RAG-Wissen und überwachten Fehlermustern. KI wird Teil des Prozesses, nicht Spielerei.',
          },
          integrations: {
            title: 'Anbindungen',
            body: 'eBay, BrickLink, PayPal, Slack und eigene APIs in einem Datenmodell. Marktplätze werden angebunden, nicht nachgepflegt. Optional dazu: unser eigenes kombiniertes Shop-Modell als zusätzlicher Verkaufskanal.',
          },
          automation: {
            title: 'Automatisierung',
            body: 'Fulfillment-Agenten, geplante Jobs und Überwachung je Dienst. Meldewege und Reaktionszeiten werden für den jeweiligen Dienst vereinbart.',
          },
        },
        pricing: {
          title: 'Preise',
          colService: 'Leistung',
          colScope: 'Umfang',
          colPrice: 'Ab',
          note: 'Alle Preise zzgl. USt. Grundlage ist ein gemeinsam abgestimmter Umfang — keine versteckten Posten, keine Lizenzbindung.',
          rows: {
            dev: { service: 'KI-Workflow-Entwicklung', scope: 'nach Aufwand', price: '125,00 €/h' },
            integration: { service: 'Anbindung je Schnittstelle', scope: 'Festpreis nach Umfang', price: 'auf Anfrage' },
            ops: { service: 'Betrieb und Überwachung', scope: 'je Dienst', price: '240,00 €/Monat' },
          },
        },
        stepsTitle: 'Ablauf',
        steps: {
          talk: {
            title: 'Erstgespräch',
            body: '30 Minuten, kostenlos. Wir klären, wo KI und Automatisierung in Ihrem Betrieb wirklich Aufwand sparen.',
          },
          pilot: {
            title: 'Pilot',
            body: 'Ein abgegrenzter Workflow mit gemeinsam vereinbarten Test- und Abnahmekriterien. Terminplanung nach Scope-, Rechte- und Kapazitätsprüfung.',
          },
          ops: {
            title: 'Betrieb',
            body: 'Wir betreiben, überwachen und erweitern. Abrechnung je Dienst, monatlich kündbar.',
          },
        },
        contact: {
          title: 'Bereit für den ersten Workflow?',
          body: 'Schreiben Sie uns, welcher Prozess Sie am meisten Zeit kostet — wir antworten mit einem konkreten Vorschlag.',
          cta: 'kontakt@weretrade.com',
          secondary: 'Eigene Projekte & Plattform ansehen',
        },
      },
      zuruf: {
        name: 'Ansicht auf Zuruf',
        badge: 'Im Test',
        eyebrow: 'weretradeIT · Arbeitsoberflächen',
        title: 'Eine Frage. Eine Ansicht.',
        lede: 'Statt eines Bildschirms, der alles zeigt, entsteht die Ansicht aus der Frage. In Sekunden, und nur mit dem, was für diese eine Aufgabe zählt.',
        alt: 'Animation: Ein voller Bildschirm mit Menü, Filtern, Kennzahlen und einer Tabelle mit neun Spalten. Eine Frage wird eingetippt. Alles, was die Frage nicht braucht, verschwindet. Übrig bleibt eine einzige Ansicht mit der Antwort.',
        placeholder: 'Was willst du wissen?',
        note: 'gebraucht wird: 1 von 9 Spalten',
        count: 'Elemente auf dem Schirm',
        time: '≈ 2 s',
        v1kicker: 'Nirgends gelistet',
        v1sub: '14 von 30 Artikeln stehen auf keinem Kanal.',
        v1q: '2 Zahlen aus der Datenbank',
        share: '46,7',
        v2kicker: 'Neu gegen gebraucht',
        v2a: 'neu',
        v2b: 'gebraucht',
        v2an: '101 Stück · 72,1 %',
        v2bn: '39 Stück · 27,9 %',
        v2q: '1 Gruppierung aus der Datenbank',
        v3kicker: 'Bestseller der letzten Woche',
        v3p1: '5 Stück',
        v3p2: '4 Stück',
        v3p3: '3 Stück',
        v3n1: 'Kartenspiel',
        v3n2: 'Tierfigur',
        v3n3: 'Zug, Bausatz',
        v3s1: '34,50 € Umsatz',
        v3s2: '35,96 € Umsatz',
        v3s3: '37,50 € Umsatz',
        v3q: '1 Rangliste aus den Verkäufen',
        end1: 'Eine Frage. Eine Ansicht.',
        end2: 'Nur was dafür zählt.',
        pause: 'Pause',
        play: 'Weiter',
        restart: 'Von vorn',
        next: 'Nächste Frage',
        p1h: 'Weniger auf dem Schirm',
        p1: 'Menüs, Spalten und Kennzahlen, die diese Frage nicht braucht, sind nicht da. Der Blick bleibt bei der Aufgabe.',
        p2h: 'Zahlen aus der Quelle',
        p2: 'Das Sprachmodell baut die Ansicht. Die Zahlen rechnet die Datenbank, nie das Modell.',
        p3h: 'Gebaut, geprüft, abgeschottet',
        p3: 'Jede erzeugte Ansicht läuft durch eine Prüfung und dann in einer abgeschlossenen Umgebung ohne Netzzugang.',
        p4h: 'Der nächste Schritt',
        p4: 'Die Ansicht kann anbieten, was als Nächstes ansteht. Den Knopf stellt die Anwendung, und geschrieben wird erst nach einer Rückfrage.',
        foot: 'Beispieldaten. Stand Oktober 2026: als Testfläche im Betrieb, für Bestand und Verkäufe.',
        liveh: 'Aus dem Test: die echte Oberfläche',
        livep: 'Eine unbeschleunigte Aufnahme. Die Stoppuhr unten im Bild misst im Browser, vom Abschicken der Frage bis zur fertigen Ansicht.',
        livealt: 'Aufnahme: Sechs Fragen werden nacheinander eingetippt. Es entstehen eine Rangliste der meistverkauften Artikel, ein Umsatzverlauf über dreißig Tage und Balken je Verkaufskanal. Ein Klick auf einen Kanal grenzt die Ansicht ein. Für die drei neuesten Artikel bietet die Seite an, Auftragsentwürfe anzulegen, und fragt vorher nach. Eine wiederholte Frage ist sofort da. Eine Frage nach der Marge wird ehrlich abgelehnt.',
        f1h: 'Neue Frage',
        f1v: 'rund 2 s',
        f1s: 'Median aus 54 Messungen über den ganzen Weg, 53 von 54 Antworten richtig.',
        f2h: 'Dieselbe Frage erneut',
        f2v: '0,3 s',
        f2s: 'Die geprüfte Ansicht liegt bereit. Die Zahlen kommen frisch aus der Datenbank.',
        f3h: 'Klick auf eine Gruppe',
        f3v: 'sofort',
        f3s: 'Dieselbe Ansicht, nur für diese Marke oder diesen Verkaufskanal. Dafür wird kein Sprachmodell gefragt.',
        q1: 'wie viel prozent unserer artikel sind noch nirgends gelistet',
        q2: 'neu gegen gebraucht, mit stückzahlen',
        q3: 'bestseller der letzten woche mit umsatz',
        moreLabel: 'Weiter lesen',
        moreNote: 'Die Testfläche ist Teil von inventarOwl.',
        moreOwl: 'inventarOwl ansehen',
        moreHub: 'Alle Beiträge von weretradeIT',
        teaserCta: 'Zum Beitrag',
      },
      entwicklung: {
        badge: 'weretradeIT • Automation',
        title: 'Selbstlernende Entwicklung',
        lead: 'Wie wir ein System entwickelt haben, das sich selbst verbessert. Jeder Bug macht uns stärker. Jede Session hinterlässt Wissen. Das Ergebnis: systematische Fehlervermeidung durch gelernte Muster.',
        vision: {
          title: 'Die Vision: Autonome Entwicklung',
          kicker: 'Philosophy',
          body: 'Traditionelle Softwareentwicklung ist reaktiv: Ein Bug tritt auf, wir fixen ihn, er tritt woanders wieder auf. Bei weretradeIT haben wir das umgedreht.',
          quote: '"Jede Session sollte das Projekt schlauer zurücklassen als vorher."',
          body2: 'Unser Ziel: Ein System, das aus jedem Fehler lernt und automatisch verhindert, dass derselbe Fehler erneut auftritt. Nicht durch mehr Disziplin, sondern durch bessere Automatisierung.',
        },
        quality: {
          title: 'Automatisierte Quality Gates',
          kicker: 'Testing',
          intro: 'Bevor Code live geht, durchläuft er 6 automatisierte Prüfphasen:',
          ts: 'Statische Typenprüfung für alle {apps} Apps',
          lint: 'Code-Qualität und Best Practices',
          web: '21 Sites auf Runtime-Fehler geprüft',
          ui: 'Design-System-Compliance auf allen Seiten',
          integration: '36+ API-Endpoints gegen Live-Worker',
          e2e: '40+ User-Flows in echten Browsern',
        },
        recursive: {
          title: 'Rekursive Selbstverbesserung',
          kicker: 'Core Innovation',
          intro: 'Das Herzstück unserer Entwicklung: Ein System, das aus Fehlern lernt und sich selbst verbessert.',
          fixForever: '/fix_forever - Der Lernzyklus',
          fixForeverBody: 'Wenn ein Bug auftritt, fragen wir: "Warum haben unsere Tests das nicht gefangen?" Dann erweitern wir nicht nur den Fix, sondern auch die Tests UND die Dokumentation. Ergebnis: Der gleiche Fehler kann nie wieder auftreten.',
          errorPatterns: 'error-patterns.md - Kollektives Gedächtnis',
          errorPatternsBody: '{errorPatterns} dokumentierte Fehlermuster mit Symptomen, Root Cause und Fix. Jeder neue Fehler wird hier erfasst - und automatisch bei zukünftigen Sessions berücksichtigt.',
          prevention: 'Systematische Fehlervermeidung',
          patterns: 'Gelernte Muster',
          autoChecks: 'Auto-Checks',
        },
        ai: {
          title: 'KI-gestützte Entwicklung',
          kicker: 'Claude Code',
          intro: 'Claude Code ist nicht nur ein Assistent - es ist ein integraler Teil unseres Entwicklungsprozesses mit eigenen Skills und Workflows.',
          webcheck: 'Alle 6 Prüfphasen automatisch durchlaufen',
          fixForever: 'Bug fixen + Tests + Docs in einem Schritt',
          learn: 'Session-Learnings erfassen und dokumentieren',
          sessionStart: 'Kontext und Learnings laden',
          gn8: 'End-of-Day: Check, Deploy, Commit, Learn',
          outro: 'Jeder dieser Befehle triggert komplexe, mehrstufige Workflows - vollautomatisch und selbstdokumentierend.',
        },
        cicd: {
          title: 'Vollautomatisierte CI/CD Pipeline',
          kicker: 'Deployment',
          intro: 'Von Commit bis Produktion - ohne manuellen Eingriff.',
          step1: 'Pre-commit Hooks validieren Code',
          step2: 'TypeScript + Lint + Tests',
          step3: 'Cloudflare Workers & Pages',
          step4: 'Automatische Validierung',
          metrics: 'Zusätzlich: Automatische Code-Metriken-Updates, Daten-Freshness-Checks und Error-Alerting - alles ohne manuellen Trigger.',
        },
        loop: {
          title: 'Der Lernzyklus',
          kicker: 'Workflow',
          intro: 'Jede Entwicklungssession folgt einem strukturierten Lernzyklus:',
          start: 'Session Start',
          work: 'Entwicklung',
          learn: 'Lernen',
          end: 'Session Ende',
          result: 'Ergebnis: Jede Session macht das System schlauer. Fehler werden zu Verbesserungen. Wissen geht nie verloren.',
        },
        results: {
          title: 'Die Ergebnisse',
          kicker: 'Impact',
          features: 'Features Shipped',
          active: 'Apps & Workers',
          velocity: 'Avg. Velocity/App',
          momentum: 'Accelerating',
          body: 'Und das Beste: Die Velocity steigt, während der Wartungsaufwand sinkt. Je mehr das System lernt, desto schneller wird es.',
        },
        contact: {
          title: 'Interesse an unserem Ansatz?',
          kicker: 'Get in Touch',
          body: 'Wir teilen gerne unsere Erfahrungen mit selbstlernenden Entwicklungsprozessen. Ob Consulting, White-Labeling oder einfach nur ein Austausch - wir freuen uns auf Ihre Nachricht.',
        },
      },
      humanVsLlm: {
        badge: 'lair404 Research • Januar 2026',
        title: 'Human vs LLM: Bandbreite & Kostenvergleich',
        desc: 'Akademische Studie zum Vergleich menschlicher kognitiver Bandbreite mit Claude Opus 4.5. 45 Forschungsiterationen, 33 Quellen. Kernbefund: 1.929× Kostenvorteil für LLM pro Token.',
        lead: 'Akademische Studie zum Vergleich menschlicher kognitiver Bandbreite (10 bits/s) mit Claude Opus 4.5 (76,9 tokens/s). Basierend auf 45 Forschungsiterationen und 33 akademischen Quellen. Kernbefund: 1.929× Kostenvorteil für LLM pro Token.',
        keyStat: 'LLM Iterationen möglich vs 1 menschliche Iteration',
        keyStatNote: '(Typische Nutzung: 2:1 Input/Output-Verhältnis)',
        summary: {
          title: 'Zusammenfassung',
          kicker: 'Kernbefunde',
          intro: 'Für textbasierte kognitive Aufgaben bei €50/Stunde deutschen Arbeitskosten liefert Claude Opus 4.5 äquivalente Ausgabe zu 0,05% der menschlichen Kosten bei 208-facher Geschwindigkeit.',
        },
        table: {
          metric: 'Metrik',
          human: 'Mensch (Effektiv)',
          llm: 'Claude Opus 4.5',
          advantage: 'Vorteil',
          speed: 'Ausgabegeschwindigkeit',
          cost: 'Ausgabekosten',
          availability: 'Verfügbarkeit',
          input: 'Eingabeverarbeitung',
        },
        bandwidth: {
          title: 'Bandbreitenvergleich',
          kicker: 'Das 10 Bits/s Limit',
          body1: 'Bahnbrechende Forschung in Neuron (Januar 2025) von Zheng und Meister am Caltech etablierte, dass menschliche bewusste Informationsverarbeitung bei etwa 10 Bits pro Sekunde operiert.',
          quote: '"Die unerträgliche Langsamkeit des Seins" - Warum leben wir bei 10 bits/s? (Zheng & Meister, 2025)',
          human: { title: 'Menschliche Ausgabe', desc: '0,37 tokens/s effektiv (60 WPM × 37,5% Produktivität)' },
          llm: { title: 'LLM Ausgabe', desc: '76,9 tokens/s nachhaltig (verifiziert über 3 Quellen)' },
        },
        cost: {
          title: 'Kostenökonomie',
          kicker: 'Pro Million Tokens',
          intro: 'Unter Berücksichtigung deutscher Arbeitskosten (€50/Stunde) und LLM-API-Preisen ist der Kostenunterschied dramatisch:',
          humanLabel: 'Mensch (1M Tokens)',
          llmLabel: 'Claude Opus 4.5 (1M Tokens)',
          ratioLabel: 'Kostendifferenz',
          percentLabel: 'LLM Kosten vs Mensch',
          body: 'Für die Kosten einer menschlichen Aufgabeniteration (€37) kann Claude Opus 4.5 1.929 Iterationen durchführen—ermöglicht schnelles Prototyping, A/B-Tests und iterative Verfeinerung im großen Maßstab.',
        },
        inputCost: {
          title: 'Gesamtkosten: Inklusive Input-Tokens',
          kicker: 'Reale Szenarien',
          intro: 'Claude Opus 4.5 Preise: $15/1M Input + $25/1M Output. Reale Nutzung beinhaltet Kontext-Overhead, der die Gesamtkosten beeinflusst:',
          scenario: 'Szenario',
          ratio: 'Input:Output',
          totalCost: 'Gesamtkosten/1M Output',
          advantage: 'Kostenvorteil',
          outputOnly: 'Nur Output',
          minimal: 'Minimaler Kontext',
          typical: 'Typische Nutzung',
          heavy: 'Umfangreicher Kontext',
          insight: 'Kernaussage: Selbst mit 5:1 Input-Overhead behält das LLM einen 482× Kostenvorteil gegenüber menschlicher Ausgabe.',
        },
        productivity: {
          title: 'Menschliche Produktivitätsfaktoren',
          kicker: 'Multi-Source-Analyse',
          intro: 'Menschliche kognitive Leistung wird durch mehrere reale Faktoren begrenzt, verifiziert über 6+ unabhängige Studien:',
          deepWork: { title: 'Deep Work: 3-4h/Tag', desc: 'Cal Newport, Ericsson 1993, ActivTrak 2025' },
          interruptions: { title: '275 Unterbrechungen/Tag', desc: 'Microsoft 2025, N=31.000' },
          recovery: { title: '23m 15s Erholung', desc: 'UC Irvine, Gloria Mark' },
          switching: { title: '40% Task Switching', desc: 'APA, Rubinstein et al.' },
          note: 'Effektiver Produktivitätsfaktor: 37,5% (3h/8h Deep Work) × 75% (1 - Ablenkungsverlust) = 28,125% der theoretischen Kapazität',
        },
        metr: {
          title: 'Das METR-Studien-Paradox',
          kicker: 'Kritischer Kontrapunkt',
          body: 'Die METR-Studie (Dezember 2024) ergab, dass erfahrene Entwickler eine 24%ige Beschleunigung mit KI-Tools vorhersagten, aber tatsächlich 19% LANGSAMER waren. Dieses Paradox deutet darauf hin, dass kognitiver Overhead und subjektive Wahrnehmung von objektiven Messungen abweichen.',
          quote: '"Rohe Bandbreite ≠ effektive Produktivität bei komplexen kognitiven Aufgaben"',
          note: 'Hinweis: Die METR-Befunde gelten primär für erfahrene Fachleute in ihrem Fachgebiet; weniger erfahrene Mitarbeiter können dennoch von KI-Unterstützung profitieren.',
        },
        methodology: {
          title: 'Forschungsmethodik',
          kicker: '33 Quellen',
          intro: 'Diese Studie folgt einer rigorosen Multi-Source-Verifikationsmethodik:',
          typing: { title: 'Tippgeschwindigkeit: 5 Quellen', desc: 'Typing.com, PMC, WorksRated, Wonderlic, Glassdoor' },
          salary: { title: 'Gehaltsdaten: 4 Quellen', desc: 'StepStone, Glassdoor, StackFuel, Freelancermap' },
          productivity: { title: 'Produktivität: 6 Quellen', desc: 'Newport, Ericsson, ActivTrak, APQC, Voucher Cloud, My Hours' },
          llm: { title: 'LLM-Geschwindigkeit: 3 Quellen', desc: 'Artificial Analysis, LLM-Stats, CogniDownUnder' },
          research: 'Recherche',
          refinements: 'Überarbeitungen',
          sources: 'Quellen',
        },
        implications: {
          title: 'Praktische Implikationen',
          kicker: 'Empfehlungen',
          intro: 'Basierend auf unseren Erkenntnissen sollten Organisationen erwägen:',
          automation: { title: 'Automatisierung priorisieren', desc: 'Hochvolumige, wenig komplexe Textaufgaben sind ideal für LLM-Automatisierung' },
          human: { title: 'Menschlicher Fokus', desc: 'Menschliche Ressourcen für Urteilsvermögen, Kreativität und Beziehungsaufbau reservieren' },
          hybrid: { title: 'Hybride Workflows', desc: 'LLM-Durchsatz mit menschlicher Qualitätskontrolle und strategischer Ausrichtung kombinieren' },
          oversight: { title: 'Aufsicht beibehalten', desc: 'Menschliche Expertise essentiell für kritische Entscheidungen und Halluzinationserkennung' },
        },
        cta: {
          whitepaper: 'Vollständiges Whitepaper (33 Quellen)',
          presentation: 'Interaktive Präsentation',
        },
        contact: {
          title: 'Fragen oder Zusammenarbeit',
          kicker: 'Kontakt',
          body: 'Interesse an der Diskussion der Ergebnisse, Anforderung der Rohdaten oder Zusammenarbeit bei weiterer Forschung?',
        },
        backLink: 'Zurück zu weretradeIT',
      },
    },
    contact: {
      form: {
        title: 'Kontaktformular',
        kicker: 'Nachricht senden',
        name: 'Name',
        namePlaceholder: 'Dein Name',
        email: 'E-Mail',
        emailPlaceholder: 'deine@email.de',
        subject: 'Betreff',
        subjectPlaceholder: 'Worüber möchtest du sprechen?',
        defaultSubject: 'Kontaktanfrage',
        message: 'Nachricht',
        messagePlaceholder: 'Deine Nachricht an uns...',
        submit: 'Nachricht senden',
        sending: 'Sende...',
        success: 'Danke für deine Nachricht! Wir melden uns in Kürze.',
        sendAnother: 'Weitere Nachricht senden',
        captchaRequired: 'Bitte bestätige, dass du kein Roboter bist.',
        requiredFields: 'Bitte fülle alle Pflichtfelder aus.',
        errorGeneric: 'Senden fehlgeschlagen. Bitte versuche es später erneut.',
        errorNetwork: 'Netzwerkfehler. Bitte prüfe deine Verbindung.',
        privacy: 'Deine Daten werden nur zur Beantwortung deiner Anfrage verwendet.',
        privacyLink: 'Datenschutz',
      },
    },
    bkb: {
      badge: 'Brand',
      title: 'BauKlotzBude',
      lead: 'BauKlotzBude ist deine Anlaufstelle für gebrauchte LEGO Sets, Einzelteile und Technic. Professionell gereinigt, sorgfältig sortiert und versandbereit. Hier findest du alle Artikel rund um unsere LEGO-Shops und Prozesse.',
      about: {
        title: 'Über BauKlotzBude',
        kicker: 'About',
        copy: 'BauKlotzBude ist die E-Commerce-Marke der weretrade IT UG. Wir verkaufen gebrauchte LEGO-Steine, Sets und Technic-Teile auf BrickLink und eBay. Jeder Stein wird professionell gereinigt und geprüft, bevor er zu dir kommt.',
        features: {
          cleaning: 'Professionelle Reinigung: Ultraschallbäder & getrennte Chargen',
          quality: 'Qualitätskontrolle: Sortierung nach Zustand & Funktionalität',
          shipping: 'Schneller Versand: Sicher verpackt, zuverlässig geliefert',
          ratings: 'Positive Ratings: Tausende zufriedene Käufer auf BrickLink & eBay',
        },
      },
      shops: {
        title: 'Unsere Shops',
        kicker: 'Marktplätze',
        copy: 'Finde uns auf den führenden LEGO-Marktplätzen:',
      },
      stats: {
        title: 'Öffentliche Kennzahlen',
        kicker: 'Transparenz',
        copy: 'Sieh dir unsere öffentlichen Verkaufs- und Lagerkennzahlen in Echtzeit an. Wir zeigen transparent unsere Performance auf BrickLink und eBay.',
      },
      posts: {
        title: 'Blog Posts & Artikel',
        kicker: 'Content',
        lead: 'Alle Artikel, Tipps und Einblicke rund um BauKlotzBude, LEGO-Reinigung und unsere Prozesse.',
        empty: 'Keine Artikel vorhanden. Schau bald wieder vorbei!',
        reinigung: {
          title: 'So reinigen wir LEGO-Steine',
          desc: 'Unser Reinigungsprozess für LEGO: Ultraschallbäder, getrennte Chargen, schonende Trocknung – hygienisch sauber, volle Klemmkraft.',
        },
        marktplaetze: {
          title: 'Vier Kanäle, ein Lager',
          desc: 'BrickLink, eBay, BrickOwl und der eigene Shop teilen sich einen Lagerbestand – wie der Abgleich läuft und wo seine Grenzen liegen.',
        },
        versand: {
          title: 'Nachhaltiger Versand: 200+ Pakete',
          desc: 'Seit Mai 2025 versenden wir ausschließlich mit wiederverwendetem Verpackungsmaterial – über 200 zufriedene Kunden.',
        },
      },
      versand: {
        badge: 'BauKlotzBude • Nachhaltigkeit',
        title: 'Nachhaltiger Versand: 200+ Pakete ohne neue Verpackung',
        lead: 'Seit Mai 2025 versenden wir ausschließlich mit wiederverwendetem Verpackungsmaterial – und haben dabei über 200 Pakete an zufriedene Kunden geschickt.',
        statsTitle: 'Die Zahlen',
        statsKicker: 'Fakten',
        stats: [
          { label: 'Pakete versendet', value: '200+' },
          { label: 'Neue Verpackungen gekauft', value: '0' },
          { label: 'Aktiv seit', value: '{{months}}+ Monate' },
        ],
        approachTitle: 'Unser Ansatz',
        approachKicker: 'Prozess',
        approachPoints: [
          { title: 'Sammeln', body: 'Wir sammeln Kartons, Polstermaterial und Füllmaterial aus eigenen Bestellungen und von Partnern.' },
          { title: 'Sortieren', body: 'Jedes Material wird nach Größe und Zustand sortiert – nur einwandfreie Verpackungen kommen zum Einsatz.' },
          { title: 'Wiederverwenden', body: 'Passende Kartons und Polster werden für den Versand deiner LEGO-Teile wiederverwendet.' },
        ],
        whyTitle: 'Warum das wichtig ist',
        whyKicker: 'Impact',
        whyPoints: [
          { title: 'Weniger Müll', body: 'Jeder wiederverwendete Karton ist ein Karton weniger auf der Deponie.' },
          { title: 'Ressourcenschonung', body: 'Keine neuen Bäume, kein neuer Kunststoff – wir nutzen, was schon da ist.' },
          { title: 'Kreislaufwirtschaft', body: 'LEGO-Steine werden wiederverwendet, warum nicht auch die Verpackung?' },
          { title: 'Vorbild sein', body: 'Kleine Schritte, große Wirkung – auch als kleines Unternehmen.' },
        ],
        contactTitle: 'Fragen zum Versand?',
        contactKicker: 'Kontakt',
        contactBody: 'Du möchtest mehr über unseren nachhaltigen Versand erfahren oder hast Verpackungsmaterial, das du uns spenden möchtest? Melde dich gern:',
      },
      channels: {
        badge: 'BauKlotzBude • Prozess',
        title: 'Vier Kanäle, ein Lager: Wie BauKlotzBude synchron bleibt',
        lead: 'BauKlotzBude verkauft auf BrickLink, eBay, BrickOwl und im eigenen Shop. Vier Schaufenster, ein Lagerbestand — so hängt das zusammen.',
        capsule: 'BauKlotzBude verkauft gereinigte gebrauchte LEGO-Teile auf vier Kanälen: BrickLink und eBay seit Mai 2025, BrickOwl und der eigene Shop seit 2026. Alle vier werden aus einem einzigen Lagerbestand gespeist, damit ein Verkauf auf einem Kanal die Verfügbarkeit auf den anderen sofort mitzieht.',
        fact1: 'Vier Verkaufskanäle: BrickLink, eBay, BrickOwl, eigener Shop',
        fact2: 'Ein gemeinsamer Lagerbestand statt vier gepflegter Listenstände',
        fact3: 'Gleiche Zustandsbewertung und gleicher Versand auf allen Kanälen',
        fact4: 'Abgleich in Intervallen, nicht in Echtzeit',
        problemTitle: 'Ein Stein, vier Schaufenster',
        problemKicker: 'Problem',
        problemBody: 'Dasselbe gereinigte Teil liegt bei uns genau einmal im Regal, steht aber auf vier Plattformen zum Verkauf. Wer solche Bestände von Hand pflegt, verkauft früher oder später etwas, das schon weg ist — und storniert dann Bestellungen, die eigentlich in Ordnung waren. Mit wachsendem Sortiment wird das nicht schwieriger, sondern unmöglich: Ein paar hundert Positionen kann man noch nachhalten, zehntausend nicht mehr.',
        howTitle: 'Wie der Abgleich läuft',
        howKicker: 'Prozess',
        step1: {
          title: 'Ein Lager als Wahrheit',
          body: 'Jede Charge wird nach der Reinigung genau einmal erfasst: Teil, Farbe, Zustand, Menge. Diese Erfassung ist der einzige Ort, an dem ein Bestand entsteht.',
        },
        step2: {
          title: 'Ausspielen statt Abtippen',
          body: 'Aus dem Lagerbestand gehen die Angebote an die Marktplätze und in den eigenen Shop. Kein Kanal wird von Hand gepflegt, deshalb driften die vier Schaufenster auch nicht auseinander.',
        },
        step3: {
          title: 'Verkauf zieht überall ab',
          body: 'Geht ein Teil auf einem Kanal weg, sinkt der Bestand im Lager — und damit auf allen anderen Kanälen mit. Das ist der eigentliche Grund für den ganzen Aufwand.',
        },
        step4: {
          title: 'Ein Versandprozess',
          body: 'Alle Bestellungen laufen in denselben Packtisch. Gleiche Prüfung, gleiches wiederverwendetes Verpackungsmaterial, egal wo du gekauft hast.',
        },
        channelsTitle: 'Die vier Kanäle',
        channelsKicker: 'Übersicht',
        bricklink: { role: 'Einzelteile und Sets für Sammler, seit Mai 2025.' },
        ebay: { role: 'Größere Konvolute und Sets, seit Mai 2025.' },
        brickowl: { role: 'Zweiter Teilemarktplatz, seit 2026 dabei.' },
        shop: {
          name: 'Eigener Shop',
          role: 'Unser eigenes Schaufenster ohne Marktplatzgebühren, seit 2026.',
        },
        benefitTitle: 'Was das für dich bedeutet',
        benefitKicker: 'Nutzen',
        benefit1: {
          title: 'Was angezeigt wird, ist da',
          body: 'Der häufigste Ärger im Teilehandel ist eine Bestellung, die nachträglich storniert wird, weil das Teil längst woanders verkauft war. Genau das soll der gemeinsame Bestand verhindern.',
        },
        benefit2: {
          title: 'Gleiche Ware, gleiche Beschreibung',
          body: 'Zustandsangaben kommen aus derselben Erfassung. Ein Teil ist nicht auf einem Kanal "gut" und auf dem nächsten "neuwertig".',
        },
        benefit3: {
          title: 'Du wählst den Kanal',
          body: 'Kaufe dort, wo du ohnehin unterwegs bist. Für Sammler mit Wantlist ist das meist BrickLink oder BrickOwl, für schnelle Sets eher eBay.',
        },
        benefit4: {
          title: 'Mehr Zeit für die Steine',
          body: 'Was nicht in die Pflege von vier Listings fließt, fließt in Reinigung, Sortierung und Prüfung.',
        },
        limitTitle: 'Wo die Grenzen liegen',
        limitKicker: 'Ehrlich gesagt',
        limit1: {
          title: 'Synchron heißt nicht sekundengenau',
          body: 'Der Abgleich läuft in Intervallen, nicht in Echtzeit. In seltenen Fällen gehen zwei Bestellungen auf dasselbe letzte Teil — dann melden wir uns und erstatten sofort.',
        },
        limit2: {
          title: 'Nicht jeder Kanal kann alles',
          body: 'Die Marktplätze unterscheiden sich in Kategorien, Zustandsstufen und Bildern. Wir bilden ab, was der jeweilige Kanal hergibt, statt Angaben zu erfinden.',
        },
        limit3: {
          title: 'Gebrauchte Teile bleiben Einzelstücke',
          body: 'Bei gereinigter Gebrauchtware gibt es keinen Nachschub auf Knopfdruck. Ist eine Charge weg, ist sie weg — bis die nächste durch die Reinigung läuft.',
        },
        contactTitle: 'Fragen zu Bestand oder Bestellung?',
        contactKicker: 'Kontakt',
        contactBody: 'Du suchst ein Teil, das gerade nirgends gelistet ist, oder hast eine Frage zu einer laufenden Bestellung? Schreib uns:',
      },
      roadmap: {
        title: 'Roadmap & Vision',
        kicker: 'Future',
        lead: 'Unser Weg mit BauKlotzBude: vom Start auf zwei Marktplätzen zum eigenen Shop mit automatisierter Bestandspflege.',
        start: {
          date: 'Mai 2025',
          title: 'Start auf BrickLink & eBay',
          desc: 'BauKlotzBude geht an den Start. Professionelle LEGO-Reinigung etabliert, erste Pakete versendet, erste positive Bewertungen gesammelt.',
        },
        growth: {
          date: '2025',
          title: 'Prozesse & nachhaltiger Versand',
          desc: 'Reinigung in getrennten Chargen standardisiert, Versand vollständig auf wiederverwendetes Verpackungsmaterial umgestellt, über 200 Pakete ausgeliefert.',
        },
        channels: {
          date: '2026',
          title: 'BrickOwl, eigener Shop & Automatisierung',
          desc: 'BrickOwl als dritter Marktplatz dazugekommen und der eigene Shop unter bauklotzbude.de gestartet. Bestand und Produktdaten laufen seitdem automatisiert über alle Kanäle statt per Hand.',
        },
        future: {
          date: '2027+',
          title: 'Sortiment & KI-gestützte Qualitätskontrolle',
          desc: 'Mehr LEGO Technic, seltene Sets und größere Chargen. KI-gestützte Qualitätskontrolle und Sortierung als nächster Schritt in der Reinigungsstraße.',
        },
      },
      social: {
        title: 'Folge uns auf Social Media',
        kicker: 'Social',
        copy: 'Bleib auf dem Laufenden mit neuen Angeboten, Behind-the-Scenes und LEGO-Inspiration.',
      },
      contact: {
        title: 'Kontakt & Support',
        kicker: 'Get in Touch',
        copy: 'Fragen zu Bestellungen, Versand oder Produkten? Kontaktiere uns bevorzugt über BrickLink-Nachricht oder per E-Mail.',
      },
      blogTitle: 'Artikel',
      blogKicker: 'Beiträge',
      noPosts: 'Weitere Artikel folgen in Kürze.',
    },
    growth: {
      loading: 'Lade Wachstumsdaten...',
      title: 'Messbares Wachstum durch Innovation',
      intro1: 'weretrade IT UG ist mehr als nur ein Softwareunternehmen. Wir sind ein dynamisches Team, das kontinuierlich an der Zukunft des E-Commerce arbeitet. Unsere Wachstumszahlen sprechen eine klare Sprache: Innovation, Qualität und kontinuierliche Verbesserung stehen im Mittelpunkt unserer Arbeit.',
      intro2: 'In den letzten Monaten haben wir unsere Codebasis signifikant erweitert – nicht durch willkürliches Hinzufügen von Features, sondern durch durchdachte Architekturentscheidungen, modulare Komponenten und wiederverwendbare Lösungen, die echten Mehrwert für unsere Kunden schaffen.',
      intro3: 'Jede Zeile Code, die Sie in den folgenden Grafiken sehen, repräsentiert eine Lösung für ein reales Problem, eine Optimierung für bessere Performance oder eine neue Funktion, die unseren Nutzern hilft, ihre Geschäftsziele zu erreichen.',
      linesTitle: 'Codezeilen-Entwicklung',
      linesWhy: 'Warum Codezeilen wichtig sind:',
      linesCopy: 'Eine wachsende Codebasis zeigt aktive Entwicklung und kontinuierliche Produktverbesserung. Unsere Codezeilen reflektieren neue Features, verbesserte Algorithmen und erweiterte Funktionalität für unsere E-Commerce-Plattformen.',
      filesTitle: 'Dateien im Projekt',
      filesWhy: 'Modulare Architektur:',
      filesCopy: 'Die Anzahl der Dateien zeigt unseren Fokus auf modulare, wartbare Software. Jede Komponente ist sauber getrennt, wiederverwendbar und testbar – ein Zeichen für professionelle Softwareentwicklung.',
      charsTitle: 'Zeichen Gesamt',
      charsWhy: 'Umfassende Lösungen:',
      charsCopy: 'Die Gesamtzahl der Zeichen (inklusive Kommentare, Dokumentation und Tests) zeigt den Umfang unserer Arbeit. Wir schreiben nicht nur Code – wir dokumentieren, testen und pflegen ihn professionell.',
      investTitle: 'Warum in weretrade investieren?',
      investGrowth: 'Kontinuierliches Wachstum:',
      investGrowthCopy: 'Unsere Metriken zeigen konsistente Entwicklung und stetige Produktverbesserung über mehrere Monate hinweg.',
      investQuality: 'Professionelle Qualität:',
      investQualityCopy: 'Modularer Code, umfassende Tests und saubere Architektur zeigen unseren Fokus auf nachhaltige, wartbare Software.',
      investTransparency: 'Transparenz:',
      investTransparencyCopy: 'Wir zeigen offen unsere Entwicklungsmetriken und stehen zu unseren Zahlen – ein Zeichen für Vertrauen und Professionalität.',
      investMarket: 'Marktposition:',
      investMarketCopy: 'Als Anbieter für LEGO E-Commerce-Lösungen bedienen wir eine wachsende Nische mit hohem Potenzial.',
      investTech: 'Technologie-Stack:',
      investTechCopy: 'Moderne Cloud-Infrastruktur (Cloudflare), React, TypeScript und automatisierte CI/CD-Prozesse – bereit für Skalierung.',
      ctaTitle: 'Interessiert an einer Partnerschaft?',
      ctaCopy: 'Wir suchen strategische Partner und Investoren, die unsere Vision teilen und mit uns die Zukunft des E-Commerce gestalten möchten.',
      ctaContact: 'Kontakt aufnehmen',
      ctaBack: 'Zurück zur Startseite',
      labelLines: 'Zeilen',
      labelFiles: 'Dateien',
      labelChars: 'Zeichen',
    },
    showcase: {
      accountingFox: {
        badge: 'SaaS Showcase • Finance & Compliance',
        title: 'accountingFox — Buchhaltung, die sich um sich selbst kümmert',
        lead: 'Belege scannen, Konten abstimmen, DATEV-Export erstellen — accountingFox erledigt in Minuten, was sonst Stunden dauert. Gebaut für E-Commerce-Händler, die über mehrere Marktplätze verkaufen.',
        ctaLiveDemo: 'Live-Demo ansehen ↓',
        ctaScheduleDemo: 'Demo vereinbaren ✉',
        mailtoSubject: 'accountingFox Demo anfragen',
        answerCapsule: {
          answer: 'accountingFox automatisiert die lückenlose Beleg- und Kontoabstimmung für Mehrkanal-Händler mit nativer DATEV-Kompatibilität und revisionssicherer Compliance.',
          ctaText: 'DATEV-Export im Detail ansehen',
          facts: [
            'DATEV-Buchungsstapel mit verknüpften Originalbelegen auf Knopfdruck',
            'Parallele Unterstützung für SKR03 und SKR04 ohne manuelle Umschlüsselung',
            'Automatische Abstimmung aller Marktplatz- und Zahlungsdienstleister-Abrechnungen',
            'Geldtransit-Bereinigung zur Vermeidung doppelter Umsatzerfassung',
            'Nahtlose Übergabe von Wareneinkäufen an inventarOwl für automatische Lagerfortschreibung'
          ]
        },
        screenshots: {
          heading: 'Das erwartet Sie',
          dashboardAlt: 'accountingFox Buchungsübersicht — Rechnungen, Beträge und Status auf einen Blick',
          dashboardTitle: 'Buchungsübersicht',
          dashboardDesc: 'Alle Eingangsrechnungen und ihre Status — automatisch erfasst und zugeordnet.',
          datevAlt: 'accountingFox DATEV Export — SKR03/SKR04 Buchungsstapel mit einem Klick',
          datevTitle: '1-Klick DATEV-Export',
          datevDesc: 'Buchungsstapel im DATEV-Format mit verknüpften Originalbelegen für Ihre Kanzlei.',
        },
        stats: {
          timeSavedValue: '15+ Std/Monat',
          timeSavedLabel: 'Zeitersparnis durch Automatisierung',
          skrValue: 'SKR03 & SKR04',
          skrLabel: 'Beide Kontenrahmen parallel',
          auditProofValue: '100% revisionssicher',
          auditProofLabel: 'Vollständige GoBD-Konformität',
          exportValue: '1-Klick Export',
          exportLabel: 'Fertig für DATEV Unternehmen Online',
        },
        features: {
          sectionTitle: 'Was accountingFox für Sie löst',
          receiptsKicker: 'Automatische Erfassung',
          receiptsTitle: 'Nie wieder Belege suchen',
          receiptsText: 'Rechnungen per E-Mail weiterleiten — accountingFox liest Lieferant, Betrag und Steuersatz automatisch aus. Auch ZUGFeRD- und XRechnung-Formate werden nativ verarbeitet. Kein manuelles Abtippen mehr.',
          marketplaceKicker: 'eBay, BrickLink, PayPal & Co.',
          marketplaceTitle: 'Marktplatz-Umsätze abstimmen',
          marketplaceText: 'Bei Mehrkanal-Handel fehlen oft Bankbelege für einzelne Transaktionen. accountingFox rekonstruiert monatliche Abrechnungen aus den Marktplatz-APIs und gleicht sie centgenau mit Ihren Bankauszügen ab.',
          transitKicker: 'Geldtransit-Bereinigung',
          transitTitle: 'Keine doppelten Buchungen',
          transitText: 'PayPal-Auszahlungen auf Ihr Bankkonto? accountingFox erkennt automatisch Geldtransit-Vorgänge und verhindert, dass derselbe Umsatz doppelt erfasst wird — ein klassischer Fehler bei Mehrkanal-Buchführung.',
          taxAdvisorKicker: 'DATEV-kompatibel',
          taxAdvisorTitle: 'Steuerberater-Übergabe in Minuten',
          taxAdvisorText: 'Ihr Steuerberater öffnet die Datei in DATEV Unternehmen Online, klickt auf einen Beleg-Link und sieht sofort das Original. Keine Ordner, keine ZIP-Archive, keine Telefonanrufe mehr.',
        },
        simulator: {
          cardTitle: 'Live-Demo: Interaktiver DATEV-Export',
          cardKicker: 'So sieht ein fertiger Buchungsstapel aus',
          cardText: 'Wählen Sie Ihren Kontenrahmen und sehen Sie, wie accountingFox automatisch korrekte Buchungssätze mit verknüpften Beleg-IDs erzeugt. Jeder Eintrag ist für Ihren Steuerberater per Klick nachvollziehbar.',
          labelSkr: 'Kontenrahmen:',
          labelEdition: 'Exportumfang:',
          btnKanzlei: 'Kanzlei-Paket',
          btnFull: 'Vollständig (72 Anhänge)',
          thDebit: 'Soll',
          thCredit: 'Haben',
          thAmount: 'Betrag',
          thText: 'Buchungstext',
          thVoucherId: 'Beleg-ID',
          sampleTransitSparkasse: 'Geldtransit Sparkasse → PayPal Auszahlung',
          samplePurchaseLego: 'Wareneinkauf LEGO Konvolut',
          sampleEbayRevenue: 'Monatsabschluss eBay Umsatzerlöse',
          samplePackaging: 'Verpackung & Versandmaterial',
          sampleTransitBank: 'Geldtransit Bank → PayPal Clearing',
          copiedBtn: '✓ Kopiert',
          notePrefix: '* Exportformat: ',
          editionKanzleiDesc: 'Kanzlei-Paket (Buchungsstapel + Belege-Archiv)',
          editionFullDesc: 'Vollständiger Audit-Export mit 72 Anhängen',
          noteSuffix: '. Jeder Beleg-Link öffnet das zugehörige Original direkt in DATEV Unternehmen Online.',
        },
        compliance: {
          kicker: 'GoBD-konform ab dem ersten Tag',
          title: 'Compliance ohne Kopfschmerzen',
          immutableTitle: 'Revisionssichere Buchführung',
          immutableText: 'accountingFox erzwingt unveränderbare Buchungen. Stornos erzeugen immer Ausgleichsbuchungen — Ihr Steuerprüfer kann jeden Vorgang lückenlos nachvollziehen. Die Verfahrensdokumentation wird automatisch aktualisiert.',
          auditTitle: 'Betriebsprüfung? Kein Problem.',
          auditText: 'Eine separate Prüfer-Ansicht gibt Steuerprüfern genau den gesetzlich vorgeschriebenen Lesezugriff — ohne Schreibrechte und ohne Zugriff auf operative Geschäftsdaten.',
        },
        faq: [
          { q: 'Wie reduziert accountingFox den Aufwand für Steuerberater?', a: 'Ihr Steuerberater erhält einen aufbereiteten DATEV-Buchungsstapel mit verknüpften Originalbelegen. Keine manuelle Zuordnung, keine Rückfragen zu fehlenden Belegen — die Übergabe ist in Minuten statt Stunden erledigt.' },
          { q: 'Ist accountingFox für E-Commerce-Händler geeignet?', a: 'Ja — accountingFox wurde speziell für Mehrkanal-Händler entwickelt, die über eBay, BrickLink, BrickOwl und eigene Webshops verkaufen. Die automatische Abstimmung aller Zahlungsströme spart durchschnittlich 15+ Stunden pro Monat.' },
          { q: 'Wie wird die rechtliche Sicherheit gewährleistet?', a: 'Alle Buchungen sind revisionssicher und GoBD-konform. Das System erzwingt Unveränderbarkeit — kein Datensatz wird gelöscht, Stornos erzeugen immer Ausgleichsbuchungen mit Prüfsummen.' },
          { q: 'Kann mein Steuerprüfer auf die Daten zugreifen?', a: 'Ja, über eine dedizierte Prüfer-Ansicht mit reinem Lesezugriff. Ihr Steuerprüfer sieht genau das, was gesetzlich vorgeschrieben ist — nicht mehr und nicht weniger.' }
        ]
      },
      inventarOwl: {
        badge: 'SaaS Showcase • ERP & Marketplace Intelligence',
        title: 'inventarOwl — Ihr Bestand. Vier Marktplätze. Null Überverkäufe.',
        lead: 'inventarOwl synchronisiert Ihren Lagerbestand in Echtzeit über eBay, BrickLink, BrickOwl und Ihren eigenen Webshop. Das integrierte marketShark Plugin zeigt Ihnen, wo Ihre Preise im Vergleich zum Markt stehen.',
        ctaMarketAnalysis: 'Marktpreis-Analyse ansehen ↓',
        ctaScheduleDemo: 'Demo vereinbaren ✉',
        mailtoSubject: 'inventarOwl Demo anfragen',
        answerCapsule: {
          answer: 'inventarOwl ist das zentrale Bestands- und Katalogsystem für Händler, die über mehrere Marktplätze verkaufen — mit integrierter Marktpreis-Intelligenz durch das marketShark Plugin.',
          ctaText: 'Marktpreis-Analyse im Detail',
          facts: [
            'Ein Bestand, vier Marktplätze — nie wieder Überverkäufe durch Echtzeit-Synchronisation',
            'Integriertes marketShark Plugin: Live-Marktpreisvergleich und Gewinnchancen-Analyse',
            'Flexible Produktstruktur: Sets, Einzelteile, Varianten und Sammelposten in einem System',
            'Steuerlich korrekte Bestandsbewertung nach dem Durchschnittskostenprinzip',
            'Nahtlose Übergabe an accountingFox für automatische Buchhaltung'
          ]
        },
        screenshots: {
          heading: 'Das erwartet Sie',
          inventoryAlt: 'inventarOwl Bestandsübersicht — Produkte, Lagerstand und Kanalstatus',
          inventoryTitle: 'Bestandsübersicht',
          inventoryDesc: 'Alle Produkte mit Lagerstand, Kanalstatus und Preisinformationen auf einen Blick.',
          marketsharkAlt: 'marketShark Marktpreis-Analyse — Preisvergleich und Gewinnchancen',
          marketsharkTitle: 'marketShark Preis-Intelligenz',
          marketsharkDesc: 'Live-Marktdaten für jeden Artikel: Wettbewerberpreise, Verkaufshistorie und Ihre Gewinnchance.',
        },
        stats: {
          oversellValue: '0% Überverkäufe',
          oversellLabel: 'Echtzeit-Bestandssynchronisation',
          channelsValue: '4 Marktplätze',
          channelsLabel: 'eBay · BrickLink · BrickOwl · Shop',
          marketDataValue: 'Live Marktdaten',
          marketDataLabel: 'Preisvergleich per marketShark',
          systemValue: '1 System',
          systemLabel: 'Sets, Teile, Varianten & Sammelposten',
        },
        features: {
          sectionTitle: 'Was inventarOwl für Sie löst',
          syncKicker: 'Echtzeit-Synchronisation',
          syncTitle: 'Kein manueller Bestandsabgleich mehr',
          syncText: 'Verkaufen Sie einen Artikel auf eBay, wird der Bestand sofort auf BrickLink, BrickOwl und im Webshop reduziert. Bestellungen werden bereits bei Eingang reserviert — nicht erst nach Zahlung.',
          pricingKicker: 'marketShark Plugin',
          pricingTitle: 'Preise mit Daten statt Bauchgefühl',
          pricingText: 'Für jeden Artikel sehen Sie auf einen Blick: Was verlangen andere? Was wurde zuletzt verkauft? Wie hoch ist Ihre Gewinnchance? Das Streudiagramm macht Preispositionierung transparent und faktenbasiert.',
          productKicker: 'Flexible Produktstruktur',
          productTitle: 'Sets, Teile und Kiloware in einem System',
          productText: 'LEGO-Händler verkaufen Sets, Einzelteile und unbekannte Konvolute. inventarOwl bildet alle Produkttypen ab — inklusive Sammelposten-Entnahme und anteiliger Kostenzuordnung.',
          valuationKicker: 'Durchschnittskostenmethode',
          valuationTitle: 'Steuerlich korrekte Bewertung',
          valuationText: 'Der Lagerbestand wird automatisch nach dem Durchschnittskostenprinzip bewertet. Bei Marktpreisrückgängen schlägt das System Abschreibungen vor — bereit für Ihre Steuererklärung.',
        },
        marketshark: {
          cardTitle: 'Live-Demo: marketShark Marktpreis-Analyse',
          cardKicker: 'So optimieren Sie Ihre Verkaufspreise',
          cardText: 'Wählen Sie einen Referenz-Artikel und sehen Sie, wie marketShark den Marktpreis analysiert. Rote Punkte zeigen aktuelle Angebote anderer Händler, blaue Rauten zeigen tatsächliche Verkäufe der letzten 6 Monate.',
          selectItem: 'Artikel wählen:',
          sampleItemDroid: 'Battle Droid (Einzelteil)',
          timeTwoDaysAgo: 'Vor 2 Tagen',
          timeToday: 'Heute, 14:22',
          timeYesterday: 'Gestern',
          kpiOfferedAvgTitle: 'Markt-Durchschnitt',
          kpiOfferedLotsSub: '{count} aktive Angebote',
          kpiSoldAvgTitle: 'Letzte Verkäufe (6M)',
          kpiSoldCountSub: '{count} Verkäufe',
          kpiOurPriceTitle: 'Ihr Preis',
          kpiVsMarketSub: '{pct}% vs. Markt',
          kpiWinRateTitle: 'Gewinnchance',
          kpiWinRateSub: 'Verkaufswahrscheinlichkeit',
          legendOffered: 'Angebote anderer Händler',
          legendSold: 'Tatsächliche Verkäufe (6M)',
          legendOurPrice: 'Ihr Preis ({price} €)',
          tooltipOffered: 'Angebot: {price} €',
          tooltipSold: 'Verkauf: {price} €',
          svgXAxis: 'Preisverteilung auf dem Marktplatz',
        },
        channels: {
          cardTitle: 'Multi-Channel Veröffentlichung',
          cardKicker: 'Ein Klick, vier Marktplätze',
          cardText: 'Steuern Sie von einem Ort aus, wo Ihre Artikel veröffentlicht werden. Filtern Sie nach einzelnen Kanälen, um den Status zu prüfen:',
          tabAll: 'Alle Kanäle',
          thChannel: 'Kanal',
          thStatus: 'Status',
          thSync: 'Synchronisation',
          thLots: 'Aktive Angebote',
          thLatency: 'Aktualisierungszeit',
          nameWebshop: 'Eigener Webshop',
          statusActive: 'Aktiv',
          statusRealtime: 'Echtzeit',
          modeEbay: 'Automatische Varianten-Synchronisation',
          modeBricklink: 'Direkter Bestandsabgleich in Echtzeit',
          modeBrickowl: 'Automatische Reservierung bei Bestellung',
          modeWebshop: 'Sofortige Bestandsaktualisierung',
        },
        faq: [
          { q: 'Wie verhindert inventarOwl Überverkäufe?', a: 'Jeder Verkauf auf einem Kanal reduziert den verfügbaren Bestand in Echtzeit auf allen anderen Kanälen. Das System reserviert Artikel bereits bei Bestelleingang, nicht erst bei Zahlungsbestätigung.' },
          { q: 'Was ist der Vorteil des marketShark Plugins?', a: 'Sie sehen auf einen Blick, wie Ihr Preis im Vergleich zum Markt positioniert ist. Das Streudiagramm zeigt aktuelle Angebote und letzte Verkäufe — so können Sie Preise datenbasiert optimieren statt aus dem Bauch heraus.' },
          { q: 'Wie funktioniert die Bewertung für die Steuererklärung?', a: 'inventarOwl berechnet gewogene Durchschnittskosten automatisch und berücksichtigt das Niederstwertprinzip. Bei Marktpreisrückgängen werden Abschreibungen automatisch vorgeschlagen.' },
          { q: 'Kann ich auch Sammelposten und Kiloware verwalten?', a: 'Ja — Konvolute und Kiloware werden als Sammelposten erfasst. Über ein visuelles Entnahme-Cockpit lösen Sie Einzelteile heraus, die automatisch anteilig bewertet werden.' }
        ]
      },
      marketShark: {
        badge: 'SaaS Showcase • Preis- & Arbitrage-Intelligenz',
        title: 'marketShark — Preise mit Daten statt Bauchgefühl. Arbitrage in Echtzeit.',
        lead: 'Echtzeit-Preisaggregation über BrickLink, BrickOwl und eBay. Berechnen Sie mengengewichtete Mediane, filtern Sie spekulative Ausreißer in Millisekunden und schöpfen Sie maximale Margen mit automatisierter Arbitrage-Erkennung ab.',
        ctaLiveRadar: 'Arbitrage-Radar testen ↓',
        ctaScheduleDemo: 'Demo vereinbaren ✉',
        mailtoSubject: 'marketShark Demo anfragen',
        answerCapsule: {
          answer: 'marketShark ist die zentrale Marktpreis- und Arbitrage-Engine für den professionellen LEGO- und Spielwarenhandel — mit Live-Anbindung an BrickLink OAuth 1.0a, Ausreißer-Bereinigung und automatischem Delta-Repricing.',
          ctaText: 'Arbitrage-Radar im Detail',
          f1: 'Mengengewichteter Median über 6-Monats-Verkaufshistorien statt verzerrter Durchschnittspreise',
          f2: 'Automatische Ausreißer-Filterung (< 20% oder > 500% vom elastischen Referenzpreis)',
          f3: 'Part-Out Value (POV) Zerlegung: Konvolute und Sets in Einzelteile zerlegen und Margen-Spreads berechnen',
          f4: 'Win-Rate-Algorithmus zur Ermittlung des optimalen Verkaufspreises mit > 80% Abverkaufswahrscheinlichkeit',
          f5: 'Nahtlose Übergabe an inventarOwl zur automatischen Delta-Preisanpassung auf allen Marktplätzen',
        },
        screenshotAlt: 'marketShark Marktpreis-Analyse — Preis-Scatter, Spreads und Gewinnchancen',
        screenshotTitle: 'marketShark Preis-Scatter & Arbitrage-Radar',
        screenshotDesc: 'Live-Gegenüberstellung von aktiven Angeboten und realen 6-Monats-Verkäufen mit Win-Rate-Kalkulation.',
      },
      osintOwl: {
        badge: 'SaaS Showcase • E-Commerce OSINT & Preistracking',
        title: 'osintOwl — Autonome E-Commerce OSINT, Preistracking & Marktplatz Crawler',
        lead: 'Echtzeit-Scraping über BrickLink, eBay, Amazon und Kleinanzeigen. Rotierende Residential Proxys, automatischer Anti-Bot-Bypass (Turnstile, Akamai, DataDome) und multimodale Datenextraktion für maximale Preistransparenz.',
        ctaLiveRadar: 'Scraper-Radar testen ↓',
        ctaScheduleDemo: 'Demo vereinbaren ✉',
        mailtoSubject: 'osintOwl Demo anfragen',
        answerCapsule: {
          answer: 'osintOwl ist die verteilte E-Commerce Intelligence- & Web-Scraping-Engine von weretradeIT — mit sub-500ms Edge Scrapern, TLS JA3/JA4 Fingerprint Spoofing, Camoufox Stealth Nodes und direkter Anbindung an inventarOwl & marketShark.',
          ctaText: 'Scraper-Radar im Detail',
          f1: 'Verteiltes Edge Crawling Mesh auf Cloudflare Workers mit Puppeteer Browser Rendering',
          f2: 'Anti-Bot & Captcha Resilience: 99.7% Bypass-Quote bei Cloudflare Turnstile, DataDome und Akamai',
          f3: 'TLS JA3/JA4 Fingerprint Spoofing, HTTP/2 Stream Multiplexing und Canvas/AudioContext Noise',
          f4: 'E-Commerce Schema-Normalisierung mit Zod: EAN/UPC Disambiguation und mengengewichteter Preis-Median',
          f5: 'Echtzeit-Webhooks und D1/KV Edge Storage für sofortige Repricing-Trigger in inventarOwl und marketShark',
        },
        screenshotAlt: 'osintOwl E-Commerce Scraper Cockpit — Live-Preistracking und Anti-Bot Bypass',
        screenshotTitle: 'osintOwl Scraper Radar & Edge Intelligence',
        screenshotDesc: 'Live-Gegenüberstellung von Marktplatzpreisen, Proxy-Latenzen und Anti-Bot Bypass-Raten.',
      },
      infrastructureDog: {
        badge: 'SaaS Showcase • Fleet Observability & Control',
        title: 'infrastructureDog — Volle Kontrolle über Ihre gesamte Infrastruktur',
        lead: '42 Endpunkte, automatisierte Ausfallsicherheit und KI-gesteuerte Wartung — infrastructureDog gibt Ihnen die Sicherheit, dass Ihr Business rund um die Uhr läuft.',
        ctaLiveDrill: 'DR-Drill live testen ↓',
        ctaEnterpriseDemo: 'Enterprise Demo anfragen ✉',
        mailtoSubject: 'infrastructureDog Enterprise Demo',
        answerCapsule: {
          answer: 'infrastructureDog ist der operative Kontrollraum für alle weretradeIT-Dienste — Echtzeit-Monitoring, automatisierte Ausfallsicherheit und KI-gesteuerte Systemverwaltung in einem Dashboard.',
          ctaText: 'Disaster-Recovery-Drill erleben',
          facts: [
            '42 Endpunkte über 10 Anwendungen — lückenlos überwacht im 30-Sekunden-Takt',
            'Mehrstufige Infrastruktur: Edge-Netzwerk, dedizierte Server und verschlüsseltes Mesh',
            'Ausfallsichere KI-Pools mit automatischen Provider-Fallbacks — kein Single Point of Failure',
            'Automatisierte Disaster-Recovery-Drills mit verifizierter Wiederherstellung',
            'Native KI-Agenten-Schnittstelle für autonome Systemdiagnose und Wartung'
          ]
        },
        screenshots: {
          heading: 'Das Cockpit in Aktion',
          tabsAria: 'Cockpit-Ansichten',
          tabTopology: 'System-Topologie',
          tabMatrix: 'Status-Matrix',
          tabMesh: 'Infrastruktur-Übersicht',
          topologyAlt: 'infrastructureDog System-Topologie — alle Services und Verbindungen auf einen Blick',
          topologyDesc: 'Interaktive Darstellung aller Services, Datenbanken und Verbindungen im Produktionssystem.',
          matrixAlt: 'infrastructureDog Status-Matrix — Echtzeit-Metriken und Verfügbarkeit',
          matrixDesc: 'Latenz, Verfügbarkeit und Erfolgsquoten aller Endpunkte in Echtzeit.',
          meshAlt: 'infrastructureDog Infrastruktur-Übersicht — Server, Container und Netzwerk',
          meshDesc: 'Dedizierte Server, Container-Stacks und das verschlüsselte Netzwerk-Mesh zwischen Standorten.',
        },
        stats: {
          endpointsValue: '42 Endpunkte',
          endpointsLabel: 'Lückenlose Überwachung',
          uptimeValue: '99,97% Uptime',
          uptimeLabel: 'Über alle Services hinweg',
          spofValue: 'Zero Single Point',
          spofLabel: 'Kein einzelner Ausfallpunkt',
          drValue: 'Auto-DR-Drills',
          drLabel: 'Verifizierte Wiederherstellung',
        },
        features: {
          sectionTitle: 'Warum Unternehmen auf infrastructureDog setzen',
          aiKicker: 'Zero Single Point of Failure',
          aiTitle: 'Ausfallsichere KI-Infrastruktur',
          aiText: 'Ihre KI-Services laufen über intelligente Modell-Pools statt einzelner Anbieter. Fällt ein Provider aus, übernimmt automatisch ein alternativer — Ihre Nutzer merken nichts davon.',
          monitoringKicker: 'Proaktives Monitoring',
          monitoringTitle: 'Probleme erkennen, bevor sie entstehen',
          monitoringText: 'Alle 30 Sekunden werden 42 Endpunkte überprüft. Anomalien werden erkannt und gemeldet, bevor sie Auswirkungen auf Ihre Geschäftsprozesse haben. Kein Raten, keine Überraschungen.',
          drKicker: 'Verifizierte Disaster Recovery',
          drTitle: 'Backups, die wirklich funktionieren',
          drText: 'Jedes Unternehmen hat Backups. Aber funktionieren sie im Ernstfall? infrastructureDog führt regelmäßig automatisierte Wiederherstellungstests durch — nicht nur auf dem Papier.',
          agentsKicker: 'Autonome Systemverwaltung',
          agentsTitle: 'KI-Agenten für die Routine',
          agentsText: 'Autorisierte KI-Agenten können Systemdiagnosen durchführen, Logs analysieren und Routine-Wartungen erledigen. Ihr Team konzentriert sich auf das Wesentliche.',
        },
        fleet: {
          cardTitle: 'Service-Übersicht: 42 Endpunkte im Live-Status',
          cardKicker: 'Filtern nach Kategorie',
          catAll: 'Alle (42)',
          catData: 'Daten',
          catInfra: 'Infrastruktur',
          catAi: 'KI',
          thService: 'Service',
          thDescription: 'Beschreibung',
          thStatus: 'Status',
          thLatency: 'Antwortzeit',
          thUptime: 'Verfügbarkeit',
          statusOnline: 'Online',
        },
        drDrill: {
          cardTitle: 'Live-Demo: Disaster-Recovery-Drill',
          cardKicker: 'Erleben Sie automatisierte Ausfallsicherheit',
          cardText: 'Starten Sie eine simulierte Katastrophenübung. Das System führt in Sekundenschnelle einen Backup-Restore, einen KI-Provider-Failover und einen Secrets-Vault-Check durch:',
          btnStart: 'DR-Drill starten ⚡',
          btnRunning: 'Übung läuft...',
          ariaProgress: 'DR-Drill Fortschritt',
          ariaLog: 'DR-Drill Protokoll',
          logReady: "Bereit. Klicken Sie auf 'DR-Drill starten', um die Übung zu beginnen.",
          log1: '[00:00.000] DR-Drill gestartet: Automatisierte Disaster-Recovery-Übung läuft...',
          log2: '[00:00.450] Datenbank-Snapshot erstellt und verschlüsselt archiviert ✓',
          log3: '[00:01.120] KI-Provider-Failover simuliert — Backup-Kette aktiv, +18ms Latenz ✓',
          log4: '[00:01.850] Secrets-Vault verifiziert, Schlüsselrotation bestätigt ✓',
          log5: '[00:02.340] ✅ DR-Drill abgeschlossen: 4/4 Systemebenen validiert. Datenverlust: 0.',
        },
        faq: [
          { q: 'Was passiert, wenn ein KI-Anbieter ausfällt?', a: 'Nichts, was Ihre Nutzer bemerken. Unsere KI-Modelle laufen über intelligente Pools mit automatischen Fallback-Ketten. Fällt ein Anbieter aus, übernimmt innerhalb von Millisekunden ein alternativer Provider.' },
          { q: 'Wie werden Backups und Disaster Recovery sichergestellt?', a: 'Automatisierte nächtliche Datenbank-Snapshots mit verschlüsselter Langzeitarchivierung. Regelmäßige DR-Drills verifizieren die Wiederherstellbarkeit — nicht nur auf dem Papier, sondern durch echte Test-Restores.' },
          { q: 'Können KI-Agenten die Infrastruktur selbstständig verwalten?', a: 'Ja. Über eine native Schnittstelle können autorisierte KI-Agenten Systemdiagnosen durchführen, Log-Analysen starten und Routine-Wartungsaufgaben erledigen — ohne manuelle SSH-Sitzungen.' },
          { q: 'Wie wird die Verfügbarkeit über 99,9% gehalten?', a: 'Durch Mehrfachredundanz auf jeder Ebene: Globale Edge-Verteilung, verschlüsseltes Multi-Standort-Mesh, und automatische Failover-Tests. infrastructureDog überwacht sich selbst und meldet Anomalien, bevor sie zum Problem werden.' }
        ]
      }
    },
    sisterApps: {
      title: 'Weitere Kern-Apps im weretradeIT Ökosystem:',
      accountingFox: 'accountingFox (GoBD & DATEV)',
      inventarOwl: 'inventarOwl + marketShark Plugin',
      marketShark: 'marketShark (Preis- & Arbitrage-Radar)',
      osintOwl: 'osintOwl (OSINT & Scraper Radar)',
      infrastructureDog: 'infrastructureDog (Full Control)',
      plattform: 'Plattform-Architektur',
    },
  },
  en: {
    nav: {
      home: 'Home',
      about: 'Our team',
      shop: 'Shop',
      stats: 'Stats',
      login: 'Login',
      platform: 'Platform',
      cleaning: 'Cleaning',
      imprint: 'Imprint',
      privacy: 'Privacy',
    },
    platform: {
      badge: 'Platform • Deep Dive',
      title: 'Our Platform & Internal Apps',
      lead:
        'Behind the scenes: serverless on Cloudflare with single sign-on, shared UI components, and central services for metrics, logging, and email.',
      archTitle: 'Architecture',
      archKicker: 'Cloudflare Native',
      toolsTitle: 'Core Apps',
      toolsKicker: 'Tools',
      uiTitle: 'UI & Screenshots',
      uiKicker: 'Design',
      contactTitle: 'Contact & Demos',
      contactKicker: 'Partners',
      coreApps: {
        title: 'Core Apps & Commerce',
        kicker: 'Production',
        points: [
          { title: 'ShopBeaver', body: 'Modern React/Vite storefront.' },
          { title: 'InventarOwl', body: 'Centralized inventory sync and logistics worker.' },
          { title: 'AccountingFlare', body: 'Finance & reports hub (PayPal/eBay/BrickLink), D1 + R2.' },
          { title: 'Internal Dashboard', body: 'KPI monitor, service links, version overview.' },
          { title: 'Services Portal', body: 'Docs, logging, vault, brief library — all in one surface.' }
        ]
      },
      infrastructureHubs: {
        title: 'Multi-Node Mesh Hubs',
        kicker: 'Infrastructure',
        points: [
          { title: 'lair404', body: 'Primary business node (Docker, Grafana, Vault, Honeypot).' },
          { title: 'n1njanode', body: 'Private/family node (RAG API, mail-zero, MCP servers).' },
          { title: 't3chnodrome', body: 'Private guard node protecting the n1njanode ecosystem.' },
          { title: 'd1mension503', body: 'High-Availability guard post ecosystem.' },
          { title: 'h0menode', body: 'Local Caddy Mesh Gateway & Cloudflare SSO.' }
        ]
      },
      aiAgentLayer: {
        title: 'AI & Agent Layer',
        kicker: 'Intelligence',
        points: [
          { title: 'LiteLLM Proxy', body: 'Unified gateway featuring Logical Model Pools for 100+ integrations.' },
          { title: 'Dynamic RAG Memory', body: 'Vector-based document retrieval for distributed knowledge.' },
          { title: 'Supervisors', body: 'weretrade-supervisor & scheugenpflug-supervisor for system monitoring.' },
          { title: 'BKB Agents', body: 'Automated fulfillment agents bridging eBay & Slack.' },
          { title: 'MCP Servers', body: '{mcpServers} specialized context and action servers.' }
        ]
      },
      managedForks: {
        title: 'Managed Forks',
        kicker: 'Open Source Tools',
        points: [
          { title: 'dbgate-fork', body: 'Customized database management backend UI.' },
          { title: 'scanopy-fork', body: 'Network topology visualization.' },
          { title: 'vscode-server-fork', body: 'VS Code in the browser secured via Cloudflare Access.' },
          { title: 'n8n-fork', body: 'Custom n8n build featuring OIDC and multi-server deployments.' },
          { title: 'nextcloud-fork', body: 'Nextcloud cluster (Server, Desktop, Passwords) for h0menode.' }
        ]
      },
      uiCopy:
        'We are gradually extending the UI gallery. The design supports light/dark and uses the shared kit.',
      uiPlaceholderSub: 'Internal UI previews will be added here.',
      contactCopy:
        'Interested in partnerships, white-labeling, or module demos? We can provision demo access.',
    },
    cleaning: {
      badge: 'Cleaning • Deep Dive',
      title: 'How we clean LEGO bricks',
      lead:
        'Behind the scenes: our multi-step process for hygienic, color-stable bricks — tuned for AFOL standards.',
      processTitle: 'Process overview',
      processKicker: 'Workflow',
      whyTitle: 'Why it matters',
      whyKicker: 'Quality',
      nextTitle: 'Next steps',
      nextKicker: 'Service',
      contactTitle: 'Contact & orders',
      contactKicker: 'Questions?',
      processSteps: [
        { title: 'Inbound & sorting', body: 'Visual check, split by color/material, remove foreign parts.' },
        { title: 'Ultrasonic cleaning', body: 'Micro-vibrations lift dirt and grease without pressure or heat.' },
        { title: 'Filtered wash solutions', body: 'pH-mild, plastic-safe, low residue; no harsh chemical baths.' },
        { title: 'Rinse & dry', body: 'Filtered air, gentle temperature; separated batches avoid abrasion.' },
        { title: 'Finish & QA', body: 'Manual sampling, clutch-power check, dust-free packing.' },
      ],
      whyPoints: [
        { title: 'Full clutch power', body: 'Clean pins/studs mean stable MOCs.' },
        { title: 'No micro-scratches', body: 'Gentle media, no hard brushes.' },
        { title: 'Color stability', body: 'Separate baths by color/material to avoid staining.' },
        { title: 'Hygiene', body: 'Dust, nicotine, oil — all removed before parts ship.' },
      ],
      nextCopy:
        'We are preparing a photo gallery of the cleaning line. Once ready, it will appear here.',
      bannerPill: 'Screenshot (placeholder)',
      bannerSub: 'Cleaning line & ultrasonics in action — coming soon.',
      contactCopy:
        'Want us to clean larger batches or have questions? Reach out:',
    },
    stats: {
      title: 'Stat Surrender',
      subtitle: 'Public, reduced metrics',
      updated: 'Updated',
      overview: 'Overview',
      ytd: 'Year to date',
      d30: 'Last 30 days',
      eur: 'Revenue (EUR)',
      orders: 'Orders',
      items: 'Items sold',
      shipping: 'Shipping (EUR)',
      distOrders: 'Order split',
      distRevenue: 'Revenue split',
      stockTitle: 'Total stock',
      totalItems: 'Total items',
      totalLots: 'Total lots',
      totalValue: 'Inventory value',
      sourceBL: 'BrickLink',
      sourceEB: 'eBay',
      visitors: 'Visitors',
      pageviews: 'Pageviews',
      uniqueVisitors: 'Unique Visitors',
      trafficSources: 'Traffic Sources',
      direct: 'Direct',
      referral: 'Referral',
      search: 'Search Engines',
      social: 'Social Media',
      platformSplit: 'Platform Breakdown',
      domain: 'Domain',
      footer: '© {{year}} BauKlotzBude · Public metrics',
    },
    order: {
      kicker: 'Order',
      title: 'Status {{id}}',
      loading: 'Loading order…',
      error: 'Order not found or failed to load.',
      status: 'Status',
      total: 'Total',
      totalSub: 'incl. shipping',
      subtotal: 'Subtotal',
      subtotalSub: 'excl. shipping',
      shipping: 'Shipping',
      shippingSub: 'estimated',
      items: 'Items',
      noItems: 'No items stored.',
    },
    login: {
      login: 'Login',
      register: 'Register',
      forgot: 'Forgot password',
      reset: 'Reset password',
      loginLead: 'Sign in with your credentials.',
      registerLead: 'Create a new account (whitelisted emails only).',
      forgotLead: 'We will send you a reset link.',
      resetLead: 'Set a new password.',
      email: 'Email',
      password: 'Password',
      passwordConfirm: 'Confirm password',
      submitLogin: 'Login',
      submitRegister: 'Register',
      submitForgot: 'Send link',
      submitReset: 'Set password',
      backToLogin: 'Back to login',
      alreadyAccount: 'Already have an account? Login',
      newHere: 'New here? Register',
      forgotLink: 'Forgot password?',
      allowlistNote: 'Emails are checked against the allowlist.',
      mismatch: 'Passwords do not match.',
      forgotMsg: 'If an account exists, we sent you a link.',
      resetMsg: 'Password set. You can log in now.',
      noToken: 'No reset token found. Please use the link from the email.',
      footer: 'Login, registration, forgot and reset live here together.',
      actionFailed: 'Action failed',
    },
    landing: {
      heroBadge: 'BrickCommerce & IT',
      heroTitle: 'Simple. Clear. Direct.',
      heroLead:
        'Official landing page of weretrade IT UG — the hub for our shops, IT services, and contact channels.',
      // Numbers as {placeholders}: the page supplies live metrics so DE and EN
      // cannot drift apart. See data/claims.ts.
      capsule:
        'weretrade IT UG is a German technology company operating BauKlotzBude (used LEGO on BrickLink, eBay, BrickOwl and our own shop) and {apps} AI-powered web applications on Cloudflare infrastructure. The platform combines e-commerce with systematic error prevention through learned patterns and serverless edge architecture for maximum cost efficiency.',
      fact1Term: 'Marketplaces',
      fact1Detail: 'BrickLink, eBay, BrickOwl and our own shop — from one inventory',
      fact2Term: 'Platform',
      fact2Detail: '{apps} production apps and {workers} backend services on Cloudflare',
      fact3Term: 'Development',
      fact3Detail: 'AI-augmented with Claude Code',
      fact4Term: 'Base',
      fact4Detail: 'Beratzhausen, Germany',
      capsuleCta: 'Learn about weretrade IT',
      ctas: {
        bricklink: 'Shop',
        ebay: 'Shop',
        shop: '🛍 BauKlotzBude Shop',
        intranet: '🔐 WebOS / Login',
        stats: '📈 Public Stats',
        google: '📍 Google Business Profile',
      },
      amazonPill: 'Amazon Partner',
      amazonPlaceholder: 'Hero banner 1200×200 (placeholder)',
      amazonNote: 'Enable marketing cookies to see the Amazon hero (toys/LEGO).',
      amazonDisclosure: 'As an Amazon partner we earn from qualifying purchases.',
      shopsKicker: 'Shops',
      shopsTitle: 'Our marketplaces',
      servicesKicker: 'Services',
      servicesTitle: 'IT & internal tools',
      servicesCopy:
        'We build internal services (logging, credentials vault, brief library) on Cloudflare. Reach out for partnerships or contract work.',
      servicesList: ['Serverless edge APIs', 'React frontends & design system', 'Automation & data pipelines'],
      servicesCta: 'View public metrics',
      servicesKiCta: 'Explore AI & automation',
      showcasesTitle: 'SaaS App Showcases',
      showcaseAccountingFox: 'accountingFox (GoBD & DATEV)',
      showcaseInventarOwl: 'inventarOwl + marketShark',
      showcaseInfrastructureDog: 'infrastructureDog (Full Control)',
      contactKicker: 'Contact',
      contactTitle: 'How to reach us',
      contactPref: 'Prefer BrickLink message. Email also works.',
      contactAddress: 'Address: Eichenstraße 14, 93176 Beratzhausen, Germany',
      socialKicker: 'Social',
      socialTitle: 'Latest posts',
      socialHighlight: 'Highlight post',
      socialNewest: 'Newest post',
      socialPlaceholder: 'We will show a curated social post here (Instagram/Pinterest/Facebook).',
      socialPlaceholder2: 'Will auto-fill with the latest post once the feed is wired.',
      codeKicker: 'Code Metrics',
      codeTitle: 'Repo stats',
      codeLead: 'Automatically measured: total lines, characters, and files of the monorepo.',
      codeStatusWarn: 'Live data unavailable right now – showing estimates.',
      advKicker: 'Advertiser Snapshot',
      advTitle: 'Reach & quality',
      advLead: 'Combined visibility from BrickLink + eBay. Ideal for partner or campaign placements.',
      advWarn: 'Live APIs not reachable · showing estimates',
      advPositives: 'Positive ratings',
      advVisits: 'Monthly visits',
      advCtr: 'CTR campaigns',
      advConversion: 'Conversion',
      advNote: 'Live ratings come from our marketplace APIs; traffic/CTR are conservative benchmarks from recent campaigns.',
      platformKicker: 'Platform',
      platformTitle: 'Our internal apps',
      platformCopy:
        'Our stack runs fully serverless on Cloudflare. We use a shared design system, single sign-on, and central services for logging, metrics, and email.',
      platformList: [
        'Internal Dashboard: KPI monitoring, service links, team shortcuts.',
        'AccountingFlare: Finance/report hub with D1 + R2, BrickLink/eBay/PayPal analytics.',
        'Services Portal: Docs, logging, brief library, credentials vault – all in one surface.',
        'Inventar Owl: Inventory worker + Pages frontend for stock and files.',
        'Code Metrics & Feedback: Public metrics, anonymized feedback, and status badges.',
      ],
      platformScreenshotPill: 'Screenshots (placeholder)',
      platformScreenshotSub: 'Internal UI previews go here – protected by login/SSO.',
      platformNote: 'Access to internal apps goes through the frontdoor login. Ask us for demos or partner access.',
      readMore: 'Read more',
      cleaningKicker: 'Cleaning',
      cleaningTitle: 'How we clean LEGO bricks',
      cleaningCopy:
        'Clean bricks are the base for happy AFOLs. We use modern methods that are gentle and thorough — from ultrasonic baths for small parts to separate drying with filtered air.',
      cleaningList: [
        'Ultrasonic: Micro-vibrations remove dust and grease without pressure or heat.',
        'Filtered wash solutions: pH-mild, plastic-safe, low residue.',
        'Separated batches: Sorted by color/material to avoid abrasion and staining.',
        'Drying & finish: Filtered-air drying, final visual check, and packing.',
      ],
      cleaningScreenshotPill: 'Screenshot (placeholder)',
      cleaningScreenshotSub: 'Photo of the cleaning line is coming soon.',
      cleaningResult: 'Result: Hygienic bricks, full clutch power, no micro-scratches — ready for your next MOC.',
      feedbackKicker: 'Feedback',
      feedbackTitle: 'Customer satisfaction',
      feedbackLead: 'Snippets from BrickLink & eBay (anonymized).',
      feedbackLabel: 'Positive reviews',
      adsensePill: 'Google AdSense',
      adsenseTitle: '',
      adsenseNote: 'Enable marketing cookies to see personalised ads.',
      footerMade: 'made with ❤️ weretradeIT © {{year}}',
      footerSocials: 'Social channels',
    },
    wt: {
      badge: 'Brand',
      title: 'weretradeIT',
      lead: 'weretradeIT stands for modern IT solutions, serverless architecture, and innovative development. Here you find all articles and updates about our technology platform.',
      about: {
        title: 'About weretradeIT',
        kicker: 'About',
        copy: 'weretradeIT is the IT brand of weretrade IT UG (haftungsbeschränkt). We build production software with AI-augmented development instead of large teams: {apps} apps and {workers} services run fully serverless on Cloudflare Workers, Pages, D1 and R2 — with no server anyone has to patch at night. We put the same way of working into client e-commerce platforms, internal tools and process automation. What that model costs and what it produces is shown by the two widgets further down — with the arithmetic on display.',
      },
      tech: {
        title: 'Built With',
        kicker: 'Technologies',
        copy: 'Our platform uses cutting-edge technologies for performance, scalability, and developer experience.',
      },
      posts: {
        title: 'Blog Posts & Articles',
        kicker: 'Content',
        lead: 'All articles, tutorials, and insights about weretradeIT and our technology platform.',
        empty: 'No articles yet. Check back soon!',
        plattform: {
          title: 'Our Platform & Internal Apps',
          desc: 'A deep dive into our Cloudflare-based architecture, shared UI, auth system, and internal tools.',
        },
        entwicklung: {
          title: 'Self-Learning Development',
          desc: 'How we built a system that improves itself. Systematic error prevention through learned patterns.',
        },
        ki: {
          title: 'AI, Integrations & Automation',
          desc: 'AI workflows, marketplace integrations, and automation for merchants — built and operated by weretradeIT.',
        },
        zuruf: {
          title: 'View on demand: One question. One view.',
        },
      },
      contact: {
        title: 'Contact & Services',
        kicker: 'Get in Touch',
        copy: 'Interested in our IT services, white-labeling, or partnerships? We also develop custom solutions.',
      },
      roadmap: {
        title: 'Research Phases 2025–2027+',
        kicker: 'Vision',
        lead: 'Our research journey: From experimental start in May 2025 to data-driven platform.',
        start: {
          date: 'May 2025',
          title: 'Foundation',
          desc: 'weretrade IT UG founded. Started developing self-learning systems.',
        },
        q1_2026: {
          date: 'Q1 2026',
          title: 'Public Self-Improving Services',
          desc: 'Release of generic, self-learning services: Ask anything, get better output from day to day.',
        },
        phase_2027: {
          date: '2027+',
          title: 'Research Phase 2',
          desc: 'Extended data collection, algorithm evaluation, collaboration with research partners for long-term studies.',
        },
      },
      blogTitle: 'Articles',
      blogKicker: 'Posts',
      noPosts: 'More articles coming soon.',
      orchestration: {
        badge: 'weretradeIT • Technology',
        title: 'Why Orchestration Beats Fine-Tuning',
        desc: 'A smarter approach to AI: Instead of investing millions in custom models, we built an intelligent orchestration layer.',
      },
      ki: {
        badge: 'AI workflows and integrations',
        title: 'AI and automation that stays in operation.',
        lead: 'We build and operate AI workflows, marketplace integrations, and automation for mid-sized merchants. Operated on our own infrastructure, billed by effort.',
        ctaPrimary: 'Book an intro call',
        ctaSecondary: 'More about weretradeIT',
        cardsTitle: 'What we focus on',
        cards: {
          ai: {
            title: 'AI Workflows',
            body: 'Orchestration instead of fine-tuning: multi-provider gateway with automated fallbacks (Claude, GPT, Llama, Gemini), RAG knowledge, and monitored error patterns. AI becomes part of the process, not a gimmick.',
          },
          integrations: {
            title: 'Integrations',
            body: 'eBay, BrickLink, PayPal, Slack, and your own APIs in one data model. Marketplaces get connected, not maintained by hand. Optionally: our own combined shop model as an additional sales channel.',
          },
          automation: {
            title: 'Automation',
            body: 'Fulfillment agents, scheduled jobs, and per-service monitoring. Reporting channels and response times are agreed for each service.',
          },
        },
        pricing: {
          title: 'Pricing',
          colService: 'Service',
          colScope: 'Scope',
          colPrice: 'From',
          note: 'All prices plus VAT. Based on a jointly agreed scope — no hidden items, no license lock-in.',
          rows: {
            dev: { service: 'AI workflow development', scope: 'by effort', price: '€125.00/h' },
            integration: { service: 'Integration per interface', scope: 'fixed price by scope', price: 'on request' },
            ops: { service: 'Operations and monitoring', scope: 'per service', price: '€240.00/month' },
          },
        },
        stepsTitle: 'How it works',
        steps: {
          talk: {
            title: 'Intro call',
            body: '30 minutes, free. We identify where AI and automation actually save effort in your operation.',
          },
          pilot: {
            title: 'Pilot',
            body: 'A bounded workflow with agreed tests and acceptance criteria. Dates are planned after reviewing scope, rights and available capacity.',
          },
          ops: {
            title: 'Operations',
            body: 'We operate, monitor, and extend. Billed per service, cancellable monthly.',
          },
        },
        contact: {
          title: 'Ready for the first workflow?',
          body: 'Tell us which process costs you the most time — we reply with a concrete proposal.',
          cta: 'kontakt@weretrade.com',
          secondary: 'View own projects & platform',
        },
      },
      zuruf: {
        name: 'View on demand',
        badge: 'In testing',
        eyebrow: 'weretradeIT · working surfaces',
        title: 'One question. One view.',
        lede: 'Instead of a screen that shows everything, the view is built from the question. In seconds, and only with what this one task needs.',
        alt: 'Animation: a crowded screen with a menu, filters, figures and a table with nine columns. A question is typed. Everything the question does not need disappears. One single view with the answer remains.',
        placeholder: 'What do you want to know?',
        note: 'needed: 1 of 9 columns',
        count: 'elements on screen',
        time: '≈ 2 s',
        v1kicker: 'Not listed anywhere',
        v1sub: '14 of 30 items are on no sales channel.',
        v1q: '2 numbers from the database',
        share: '46.7',
        v2kicker: 'New versus used',
        v2a: 'new',
        v2b: 'used',
        v2an: '101 units · 72.1%',
        v2bn: '39 units · 27.9%',
        v2q: '1 grouping from the database',
        v3kicker: 'Best sellers of last week',
        v3p1: '5 units',
        v3p2: '4 units',
        v3p3: '3 units',
        v3n1: 'Card game',
        v3n2: 'Animal figure',
        v3n3: 'Train set',
        v3s1: '€34.50 revenue',
        v3s2: '€35.96 revenue',
        v3s3: '€37.50 revenue',
        v3q: '1 ranking from the sales',
        end1: 'One question. One view.',
        end2: 'Only what it needs.',
        pause: 'Pause',
        play: 'Resume',
        restart: 'Restart',
        next: 'Next question',
        p1h: 'Less on the screen',
        p1: 'Menus, columns and figures this question does not need are simply not there. Attention stays on the task.',
        p2h: 'Numbers from the source',
        p2: 'The language model builds the view. The numbers are computed by the database, never by the model.',
        p3h: 'Built, checked, sealed off',
        p3: 'Every generated view passes a check and then runs in a closed environment without network access.',
        p4h: 'The next step',
        p4: 'The view can offer what comes next. The button belongs to the application, and nothing is written before a confirmation.',
        foot: 'Sample data. As of October 2026: live as a test surface, for stock and sales.',
        liveh: 'From the test: the real interface',
        livep: 'An unedited recording at real speed. The stopwatch at the bottom runs in the browser, from sending the question to the finished view.',
        livealt: 'Recording: six questions are typed one after another. A ranking of best-selling items, a revenue trend over thirty days and bars per sales channel appear. A click on a channel narrows the view. For the three newest items the page offers to create order drafts and asks first. A repeated question is there at once. A question about margin is honestly declined.',
        f1h: 'New question',
        f1v: 'about 2 s',
        f1s: 'Median of 54 measurements across the full path, 53 of 54 answers correct.',
        f2h: 'Same question again',
        f2v: '0.3 s',
        f2s: 'The checked view is ready. The numbers come fresh from the database.',
        f3h: 'Click on a group',
        f3v: 'instant',
        f3s: 'The same view, for this brand or this sales channel only. No language model is asked for it.',
        q1: 'what share of our items is not listed anywhere yet',
        q2: 'new versus used, with unit counts',
        q3: 'best sellers of last week with revenue',
        moreLabel: 'Read on',
        moreNote: 'The test surface is part of inventarOwl.',
        moreOwl: 'See inventarOwl',
        moreHub: 'All weretradeIT articles',
        teaserCta: 'Read the article',
      },
      entwicklung: {
        badge: 'weretradeIT • Automation',
        title: 'Self-Learning Development',
        lead: 'How we built a system that improves itself. Every bug makes us stronger. Every session leaves knowledge behind. The result: systematic error prevention through learned patterns.',
        vision: {
          title: 'The Vision: Autonomous Development',
          kicker: 'Philosophy',
          body: 'Traditional software development is reactive: A bug occurs, we fix it, it reoccurs elsewhere. At weretradeIT, we reversed that.',
          quote: '"Every session should leave the project smarter than before."',
          body2: 'Our goal: A system that learns from every error and automatically prevents the same error from occurring again. Not through more discipline, but through better automation.',
        },
        quality: {
          title: 'Automated Quality Gates',
          kicker: 'Testing',
          intro: 'Before code goes live, it passes through 6 automated test phases:',
          ts: 'Static type checking for all {apps} apps',
          lint: 'Code quality and best practices',
          web: '21 sites checked for runtime errors',
          ui: 'Design system compliance on all pages',
          integration: '36+ API endpoints against live workers',
          e2e: '40+ user flows in real browsers',
        },
        recursive: {
          title: 'Recursive Self-Improvement',
          kicker: 'Core Innovation',
          intro: 'The heart of our development: A system that learns from errors and improves itself.',
          fixForever: '/fix_forever - The Learning Cycle',
          fixForeverBody: 'When a bug occurs, we ask: "Why didn\'t our tests catch this?" Then we expand not just the fix, but also the tests AND the documentation. Result: The same error can never occur again.',
          errorPatterns: 'error-patterns.md - Collective Memory',
          errorPatternsBody: '{errorPatterns} documented error patterns with symptoms, root cause, and fix. Every new error is captured here - and automatically considered in future sessions.',
          prevention: 'Error Prevention Rate',
          patterns: 'Learned Patterns',
          autoChecks: 'Auto-Checks',
        },
        ai: {
          title: 'AI-Augmented Development',
          kicker: 'Claude Code',
          intro: 'Claude Code is not just an assistant - it\'s an integral part of our development process with its own skills and workflows.',
          webcheck: 'Run all 6 test phases automatically',
          fixForever: 'Fix bug + tests + docs in one step',
          learn: 'Capture and document session learnings',
          sessionStart: 'Load context and learnings',
          gn8: 'End-of-Day: Check, Deploy, Commit, Learn',
          outro: 'Each of these commands triggers complex, multi-step workflows - fully automated and self-documenting.',
        },
        cicd: {
          title: 'Fully Automated CI/CD Pipeline',
          kicker: 'Deployment',
          intro: 'From commit to production - without manual intervention.',
          step1: 'Pre-commit hooks validate code',
          step2: 'TypeScript + Lint + Tests',
          step3: 'Cloudflare Workers & Pages',
          step4: 'Automatic validation',
          metrics: 'Additionally: Automatic code metrics updates, data freshness checks, and error alerting - all without manual triggers.',
        },
        loop: {
          title: 'The Learning Loop',
          kicker: 'Workflow',
          intro: 'Every development session follows a structured learning cycle:',
          start: 'Session Start',
          work: 'Development',
          learn: 'Learning',
          end: 'Session End',
          result: 'Result: Every session makes the system smarter. Errors become improvements. Knowledge is never lost.',
        },
        results: {
          title: 'The Results',
          kicker: 'Impact',
          features: 'Features Shipped',
          active: 'Apps & Workers',
          velocity: 'Avg. Velocity/App',
          momentum: 'Accelerating',
          body: 'And the best part: Velocity increases while maintenance effort decreases. The more the system learns, the faster it becomes.',
        },
        contact: {
          title: 'Interested in Our Approach?',
          kicker: 'Get in Touch',
          body: 'We\'re happy to share our experiences with self-learning development processes. Whether consulting, white-labeling, or just an exchange - we look forward to hearing from you.',
        },
      },
      humanVsLlm: {
        badge: 'lair404 Research • January 2026',
        title: 'Human vs LLM: Bandwidth & Cost Comparison',
        desc: 'Academic study comparing human cognitive bandwidth with Claude Opus 4.5. 45 research iterations, 33 sources. Key finding: 1,929x cost advantage for LLM per token.',
        lead: 'Academic study comparing human cognitive bandwidth (10 bits/s) with Claude Opus 4.5 (76.9 tokens/s). Based on 45 research iterations and 33 academic sources. Key finding: 1,929x cost advantage for LLM per token.',
        keyStat: 'LLM Iterations possible vs 1 Human Iteration',
        keyStatNote: '(Typical Usage: 2:1 input/output ratio)',
        summary: {
          title: 'Executive Summary',
          kicker: 'Key Findings',
          intro: 'For text-based cognitive tasks at €50/hour German labor rates, Claude Opus 4.5 delivers equivalent output at 0.05% of human cost while operating 208 times faster.',
        },
        table: {
          metric: 'Metric',
          human: 'Human (Effective)',
          llm: 'Claude Opus 4.5',
          advantage: 'Advantage',
          speed: 'Output Speed',
          cost: 'Output Cost',
          availability: 'Availability',
          input: 'Input Processing',
        },
        bandwidth: {
          title: 'Bandwidth Comparison',
          kicker: 'The 10 Bits/s Limit',
          body1: 'Groundbreaking research published in Neuron (January 2025) by Zheng and Meister at Caltech established that human conscious information processing operates at approximately 10 bits per second.',
          quote: '"The unbearable slowness of being" - Why do we live at 10 bits/s? (Zheng & Meister, 2025)',
          human: { title: 'Human Output', desc: '0.37 tokens/sec effective (60 WPM × 37.5% productivity)' },
          llm: { title: 'LLM Output', desc: '76.9 tokens/sec sustained (verified across 3 sources)' },
        },
        cost: {
          title: 'Cost Economics',
          kicker: 'Per Million Tokens',
          intro: 'When accounting for German labor costs (€50/hour) and LLM API pricing, the cost differential is dramatic:',
          humanLabel: 'Human (1M tokens)',
          llmLabel: 'Claude Opus 4.5 (1M tokens)',
          ratioLabel: 'Cost Differential',
          percentLabel: 'LLM Cost vs Human',
          body: 'For the cost of one human task iteration (€37), Claude Opus 4.5 can perform 1,929 iterations—enabling rapid prototyping, A/B testing, and iterative refinement at scale.',
        },
        inputCost: {
          title: 'Total Cost: Including Input Tokens',
          kicker: 'Real-World Scenarios',
          intro: 'Claude Opus 4.5 pricing: $15/1M input + $25/1M output. Real-world usage includes context overhead that affects total cost:',
          scenario: 'Scenario',
          ratio: 'Input:Output',
          totalCost: 'Total Cost/1M Output',
          advantage: 'Cost Advantage',
          outputOnly: 'Output Only',
          minimal: 'Minimal Context',
          typical: 'Typical Usage',
          heavy: 'Heavy Context',
          insight: 'Key Insight: Even with 5:1 input overhead, LLM maintains 482× cost advantage over human output.',
        },
        productivity: {
          title: 'Human Productivity Factors',
          kicker: 'Multi-Source Analysis',
          intro: 'Human cognitive output is limited by multiple real-world factors, verified across 6+ independent studies:',
          deepWork: { title: 'Deep Work: 3-4h/day', desc: 'Cal Newport, Ericsson 1993, ActivTrak 2025' },
          interruptions: { title: '275 Interruptions/day', desc: 'Microsoft 2025, N=31,000' },
          recovery: { title: '23m 15s Recovery', desc: 'UC Irvine, Gloria Mark' },
          switching: { title: '40% Task Switching Loss', desc: 'APA, Rubinstein et al.' },
          note: 'Effective Productivity Factor: 37.5% (3h/8h deep work) × 75% (1 - distraction loss) = 28.125% of theoretical capacity',
        },
        metr: {
          title: 'The METR Study Paradox',
          kicker: 'Critical Counterpoint',
          body: 'The METR study (December 2024) found that experienced developers predicted a 24% speedup with AI tools but were actually 19% SLOWER. This paradox suggests cognitive overhead and subjective perception differ from objective measurements.',
          quote: '"Raw bandwidth ≠ effective productivity in complex cognitive tasks"',
          note: 'Note: The METR findings apply primarily to experienced professionals in their domain of expertise; less experienced workers may still benefit from AI augmentation.',
        },
        methodology: {
          title: 'Research Methodology',
          kicker: '33 Sources',
          intro: 'This study follows rigorous multi-source verification methodology:',
          typing: { title: 'Typing Speed: 5 Sources', desc: 'Typing.com, PMC, WorksRated, Wonderlic, Glassdoor' },
          salary: { title: 'Salary Data: 4 Sources', desc: 'StepStone, Glassdoor, StackFuel, Freelancermap' },
          productivity: { title: 'Productivity: 6 Sources', desc: 'Newport, Ericsson, ActivTrak, APQC, Voucher Cloud, My Hours' },
          llm: { title: 'LLM Speed: 3 Sources', desc: 'Artificial Analysis, LLM-Stats, CogniDownUnder' },
          research: 'Research',
          refinements: 'Refinements',
          sources: 'Sources',
        },
        implications: {
          title: 'Practical Implications',
          kicker: 'Recommendations',
          intro: 'Based on our findings, organizations should consider:',
          automation: { title: 'Prioritize Automation', desc: 'High-volume, low-complexity text tasks are ideal for LLM automation' },
          human: { title: 'Human Focus', desc: 'Reserve human resources for judgment, creativity, and relationship-building' },
          hybrid: { title: 'Hybrid Workflows', desc: 'Combine LLM throughput with human quality control and strategic direction' },
          oversight: { title: 'Maintain Oversight', desc: 'Human expertise essential for critical decisions and hallucination detection' },
        },
        cta: {
          whitepaper: 'Full Whitepaper (33 Sources)',
          presentation: 'Interactive Presentation',
        },
        contact: {
          title: 'Questions or Collaboration',
          kicker: 'Get in Touch',
          body: 'Interested in discussing the findings, requesting the raw data, or collaborating on further research?',
        },
        backLink: 'Back to weretradeIT',
      },
    },
    contact: {
      form: {
        title: 'Contact Form',
        kicker: 'Send Message',
        name: 'Name',
        namePlaceholder: 'Your name',
        email: 'Email',
        emailPlaceholder: 'your@email.com',
        subject: 'Subject',
        subjectPlaceholder: 'What would you like to discuss?',
        defaultSubject: 'Contact inquiry',
        message: 'Message',
        messagePlaceholder: 'Your message to us...',
        submit: 'Send Message',
        sending: 'Sending...',
        success: 'Thank you for your message! We will get back to you soon.',
        sendAnother: 'Send another message',
        captchaRequired: 'Please confirm you are not a robot.',
        requiredFields: 'Please fill in all required fields.',
        errorGeneric: 'Failed to send. Please try again later.',
        errorNetwork: 'Network error. Please check your connection.',
        privacy: 'Your data will only be used to respond to your inquiry.',
        privacyLink: 'Privacy Policy',
      },
    },
    bkb: {
      badge: 'Brand',
      title: 'BauKlotzBude',
      lead: 'BauKlotzBude is your destination for used LEGO sets, parts, and Technic. Professionally cleaned, carefully sorted, and ready to ship. Here you find all articles about our LEGO shops and processes.',
      about: {
        title: 'About BauKlotzBude',
        kicker: 'About',
        copy: 'BauKlotzBude is the e-commerce brand of weretrade IT UG. We sell used LEGO bricks, sets, and Technic parts on BrickLink and eBay. Every brick is professionally cleaned and inspected before it comes to you.',
        features: {
          cleaning: 'Professional Cleaning: Ultrasonic baths & separate batches',
          quality: 'Quality Control: Sorting by condition & functionality',
          shipping: 'Fast Shipping: Securely packed, reliably delivered',
          ratings: 'Positive Ratings: Thousands of satisfied buyers on BrickLink & eBay',
        },
      },
      shops: {
        title: 'Our Shops',
        kicker: 'Marketplaces',
        copy: 'Find us on the leading LEGO marketplaces:',
      },
      stats: {
        title: 'Public Metrics',
        kicker: 'Transparency',
        copy: 'View our public sales and inventory metrics in real-time. We transparently show our performance on BrickLink and eBay.',
      },
      posts: {
        title: 'Blog Posts & Articles',
        kicker: 'Content',
        lead: 'All articles, tips, and insights about BauKlotzBude, LEGO cleaning, and our processes.',
        empty: 'No articles yet. Check back soon!',
        reinigung: {
          title: 'How We Clean LEGO Bricks',
          desc: 'Our cleaning process for LEGO: ultrasonic baths, separated batches, gentle drying – hygienically clean, full clutch power.',
        },
        marktplaetze: {
          title: 'Four Channels, One Inventory',
          desc: 'BrickLink, eBay, BrickOwl and our own shop share one stock – how the sync works and where its limits are.',
        },
        versand: {
          title: 'Sustainable Shipping: 200+ Packages',
          desc: 'Since May 2025, we ship exclusively with reused packaging materials – over 200 happy customers.',
        },
      },
      versand: {
        badge: 'BauKlotzBude • Sustainability',
        title: 'Sustainable Shipping: 200+ Packages Without New Packaging',
        lead: 'Since May 2025, we ship exclusively with reused packaging materials – and have sent over 200 packages to happy customers.',
        statsTitle: 'The Numbers',
        statsKicker: 'Facts',
        stats: [
          { label: 'Packages shipped', value: '200+' },
          { label: 'New packaging bought', value: '0' },
          { label: 'Active since', value: '{{months}}+ months' },
        ],
        approachTitle: 'Our Approach',
        approachKicker: 'Process',
        approachPoints: [
          { title: 'Collect', body: 'We collect boxes, padding, and filler material from our own orders and partners.' },
          { title: 'Sort', body: 'Each material is sorted by size and condition – only flawless packaging is used.' },
          { title: 'Reuse', body: 'Suitable boxes and padding are reused for shipping your LEGO parts.' },
        ],
        whyTitle: 'Why It Matters',
        whyKicker: 'Impact',
        whyPoints: [
          { title: 'Less Waste', body: 'Every reused box is one less box in the landfill.' },
          { title: 'Resource Conservation', body: 'No new trees, no new plastic – we use what already exists.' },
          { title: 'Circular Economy', body: 'LEGO bricks are reused, why not the packaging too?' },
          { title: 'Leading by Example', body: 'Small steps, big impact – even as a small business.' },
        ],
        contactTitle: 'Questions About Shipping?',
        contactKicker: 'Contact',
        contactBody: 'Want to learn more about our sustainable shipping or have packaging materials you want to donate? Get in touch:',
      },
      channels: {
        badge: 'BauKlotzBude • Process',
        title: 'Four Channels, One Inventory: How BauKlotzBude Stays in Sync',
        lead: 'BauKlotzBude sells on BrickLink, eBay, BrickOwl and its own shop. Four storefronts, one stock — here is how that fits together.',
        capsule: 'BauKlotzBude sells cleaned used LEGO parts across four channels: BrickLink and eBay since May 2025, BrickOwl and our own shop since 2026. All four are fed from a single inventory, so a sale on one channel immediately reduces availability on the others.',
        fact1: 'Four sales channels: BrickLink, eBay, BrickOwl, own shop',
        fact2: 'One shared inventory instead of four hand-maintained listing counts',
        fact3: 'Identical condition grading and shipping across every channel',
        fact4: 'Synchronisation runs at intervals, not in real time',
        problemTitle: 'One Brick, Four Storefronts',
        problemKicker: 'Problem',
        problemBody: 'The same cleaned part sits on our shelf exactly once, yet it is offered on four platforms. Maintain those counts by hand and sooner or later you sell something that is already gone — then cancel an order that was perfectly reasonable. As the range grows this does not get harder, it becomes impossible: a few hundred positions can still be tracked, ten thousand cannot.',
        howTitle: 'How the Sync Works',
        howKicker: 'Process',
        step1: {
          title: 'One Inventory as the Truth',
          body: 'Every batch is recorded exactly once after cleaning: part, colour, condition, quantity. That record is the only place where stock comes into existence.',
        },
        step2: {
          title: 'Published, Not Retyped',
          body: 'Listings go out to the marketplaces and to our own shop from that inventory. No channel is maintained by hand, which is why the four storefronts do not drift apart.',
        },
        step3: {
          title: 'A Sale Deducts Everywhere',
          body: 'When a part sells on one channel, stock drops in the inventory — and therefore on every other channel too. That is the actual point of the whole exercise.',
        },
        step4: {
          title: 'One Shipping Process',
          body: 'All orders land on the same packing table. Same inspection, same reused packaging material, no matter where you bought.',
        },
        channelsTitle: 'The Four Channels',
        channelsKicker: 'Overview',
        bricklink: { role: 'Individual parts and sets for collectors, since May 2025.' },
        ebay: { role: 'Larger lots and complete sets, since May 2025.' },
        brickowl: { role: 'Second parts marketplace, added in 2026.' },
        shop: {
          name: 'Own Shop',
          role: 'Our own storefront without marketplace fees, since 2026.',
        },
        benefitTitle: 'What This Means for You',
        benefitKicker: 'Benefits',
        benefit1: {
          title: 'What Is Shown Is There',
          body: 'The most common frustration in parts trading is an order cancelled after the fact because the piece had long sold elsewhere. A shared stock level is exactly what prevents that.',
        },
        benefit2: {
          title: 'Same Goods, Same Description',
          body: 'Condition details come from the same record. A part is not "good" on one channel and "like new" on the next.',
        },
        benefit3: {
          title: 'You Pick the Channel',
          body: 'Buy where you already are. Collectors with a wanted list usually prefer BrickLink or BrickOwl; eBay is the quicker route for complete sets.',
        },
        benefit4: {
          title: 'More Time for the Bricks',
          body: 'Whatever does not go into maintaining four listings goes into cleaning, sorting and inspection instead.',
        },
        limitTitle: 'Where the Limits Are',
        limitKicker: 'Honestly',
        limit1: {
          title: 'In Sync Is Not to the Second',
          body: 'Synchronisation runs at intervals, not in real time. In rare cases two orders hit the same last part — then we get in touch and refund immediately.',
        },
        limit2: {
          title: 'Not Every Channel Can Do Everything',
          body: 'The marketplaces differ in categories, condition grades and images. We map what each channel supports rather than inventing details.',
        },
        limit3: {
          title: 'Used Parts Stay One-Offs',
          body: 'With cleaned second-hand goods there is no restock at the push of a button. Once a batch is gone it is gone — until the next one comes through cleaning.',
        },
        contactTitle: 'Questions About Stock or an Order?',
        contactKicker: 'Contact',
        contactBody: 'Looking for a part that is not listed anywhere right now, or have a question about an open order? Write to us:',
      },
      roadmap: {
        title: 'Roadmap & Vision',
        kicker: 'Future',
        lead: 'How BauKlotzBude got here: from a start on two marketplaces to our own shop with automated inventory sync.',
        start: {
          date: 'May 2025',
          title: 'Launch on BrickLink & eBay',
          desc: 'BauKlotzBude goes live. Professional LEGO cleaning established, first packages shipped, first positive ratings collected.',
        },
        growth: {
          date: '2025',
          title: 'Processes & Sustainable Shipping',
          desc: 'Cleaning standardised into separated batches, shipping switched entirely to reused packaging material, more than 200 packages delivered.',
        },
        channels: {
          date: '2026',
          title: 'BrickOwl, Own Shop & Automation',
          desc: 'BrickOwl added as a third marketplace and our own shop launched at bauklotzbude.de. Inventory and product data have run automatically across all channels since then, instead of by hand.',
        },
        future: {
          date: '2027+',
          title: 'Range & AI-Assisted Quality Control',
          desc: 'More LEGO Technic, rare sets and larger batches. AI-assisted quality control and sorting as the next step in the cleaning line.',
        },
      },
      social: {
        title: 'Follow Us on Social Media',
        kicker: 'Social',
        copy: 'Stay up to date with new offers, behind-the-scenes, and LEGO inspiration.',
      },
      contact: {
        title: 'Contact & Support',
        kicker: 'Get in Touch',
        copy: 'Questions about orders, shipping, or products? Contact us preferably via BrickLink message or email.',
      },
      blogTitle: 'Articles',
      blogKicker: 'Posts',
      noPosts: 'More articles coming soon.',
    },
    growth: {
      loading: 'Loading growth data...',
      title: 'Measurable Growth Through Innovation',
      intro1: 'weretrade IT UG is more than just a software company. We are a dynamic team continuously working on the future of e-commerce. Our growth metrics speak a clear language: innovation, quality, and continuous improvement are at the core of our work.',
      intro2: 'In recent months, we have significantly expanded our codebase – not by arbitrarily adding features, but through thoughtful architectural decisions, modular components, and reusable solutions that create real value for our customers.',
      intro3: 'Every line of code you see in the following charts represents a solution to a real problem, an optimization for better performance, or a new feature that helps our users achieve their business goals.',
      linesTitle: 'Lines of Code Development',
      linesWhy: 'Why lines of code matter:',
      linesCopy: 'A growing codebase shows active development and continuous product improvement. Our lines of code reflect new features, improved algorithms, and expanded functionality for our e-commerce platforms.',
      filesTitle: 'Files in Project',
      filesWhy: 'Modular Architecture:',
      filesCopy: 'The number of files shows our focus on modular, maintainable software. Each component is cleanly separated, reusable, and testable – a sign of professional software development.',
      charsTitle: 'Total Characters',
      charsWhy: 'Comprehensive Solutions:',
      charsCopy: 'The total character count (including comments, documentation, and tests) shows the scope of our work. We don\'t just write code – we document, test, and maintain it professionally.',
      investTitle: 'Why Invest in weretrade?',
      investGrowth: 'Continuous Growth:',
      investGrowthCopy: 'Our metrics show consistent development and steady product improvement over several months.',
      investQuality: 'Professional Quality:',
      investQualityCopy: 'Modular code, comprehensive tests, and clean architecture show our focus on sustainable, maintainable software.',
      investTransparency: 'Transparency:',
      investTransparencyCopy: 'We openly show our development metrics and stand by our numbers – a sign of trust and professionalism.',
      investMarket: 'Market Position:',
      investMarketCopy: 'As a provider of LEGO e-commerce solutions, we serve a growing niche with high potential.',
      investTech: 'Technology Stack:',
      investTechCopy: 'Modern cloud infrastructure (Cloudflare), React, TypeScript, and automated CI/CD processes – ready for scaling.',
      ctaTitle: 'Interested in a Partnership?',
      ctaCopy: 'We are looking for strategic partners and investors who share our vision and want to shape the future of e-commerce with us.',
      ctaContact: 'Get in Touch',
      ctaBack: 'Back to Home',
      labelLines: 'Lines',
      labelFiles: 'Files',
      labelChars: 'Characters',
    },
    showcase: {
      accountingFox: {
        badge: 'SaaS Showcase • Finance & Compliance',
        title: 'accountingFox — Accounting That Takes Care of Itself',
        lead: 'Scan receipts, reconcile accounts, generate DATEV exports — accountingFox handles in minutes what used to take hours. Built for e-commerce sellers operating across multiple marketplaces.',
        ctaLiveDemo: 'View live demo ↓',
        ctaScheduleDemo: 'Schedule a demo ✉',
        mailtoSubject: 'Request accountingFox demo',
        answerCapsule: {
          answer: 'accountingFox automates end-to-end receipt and account reconciliation for multi-channel retailers with native DATEV compatibility and audit-proof compliance.',
          ctaText: 'View DATEV export in detail',
          facts: [
            'DATEV booking batches with linked original receipts at the touch of a button',
            'Parallel support for SKR03 and SKR04 without manual account re-mapping',
            'Automatic reconciliation of all marketplace and payment provider settlements',
            'Transit netting to prevent double-counting of revenue disbursements',
            'Seamless handoff of purchase receipts to inventarOwl for automated inventory tracking'
          ]
        },
        screenshots: {
          heading: 'What to expect',
          dashboardAlt: 'accountingFox booking overview — invoices, amounts, and status at a glance',
          dashboardTitle: 'Bookings overview',
          dashboardDesc: 'All incoming invoices and their status — automatically captured and assigned.',
          datevAlt: 'accountingFox DATEV export — SKR03/SKR04 posting batch in one click',
          datevTitle: '1-Click DATEV Export',
          datevDesc: 'Posting batch in DATEV format with linked original vouchers for your tax advisor.',
        },
        stats: {
          timeSavedValue: '15+ hrs/month',
          timeSavedLabel: 'Time saved through automation',
          skrValue: 'SKR03 & SKR04',
          skrLabel: 'Both charts of accounts in parallel',
          auditProofValue: '100% audit-proof',
          auditProofLabel: 'Full GoBD compliance',
          exportValue: '1-Click Export',
          exportLabel: 'Ready for DATEV Unternehmen Online',
        },
        features: {
          sectionTitle: 'What accountingFox solves for you',
          receiptsKicker: 'Automated capture',
          receiptsTitle: 'Never search for receipts again',
          receiptsText: 'Forward invoices by email — accountingFox automatically extracts vendor, amount, and tax rate. Native support for ZUGFeRD and XRechnung formats. No more manual typing.',
          marketplaceKicker: 'eBay, BrickLink, PayPal & Co.',
          marketplaceTitle: 'Reconcile marketplace revenue',
          marketplaceText: 'In multi-channel retail, bank statements often lack single transaction details. accountingFox reconstructs monthly settlements from marketplace APIs and reconciles them down to the cent with your bank statements.',
          transitKicker: 'Cash-in-transit clearing',
          transitTitle: 'No duplicate bookings',
          transitText: 'PayPal payouts to your bank account? accountingFox automatically detects cash-in-transit movements and prevents duplicate revenue entries — a classic pitfall in multi-channel bookkeeping.',
          taxAdvisorKicker: 'DATEV compatible',
          taxAdvisorTitle: 'Handover to tax advisor in minutes',
          taxAdvisorText: 'Your tax advisor opens the file in DATEV Unternehmen Online, clicks a receipt link, and sees the original document immediately. No folders, no ZIP archives, no phone calls.',
        },
        simulator: {
          cardTitle: 'Live Demo: Interactive DATEV Export',
          cardKicker: 'What a completed posting batch looks like',
          cardText: 'Choose your chart of accounts and see how accountingFox automatically creates correct journal entries with linked document IDs. Every record is transparent to your tax advisor in a single click.',
          labelSkr: 'Chart of Accounts:',
          labelEdition: 'Export Scope:',
          btnKanzlei: 'Firm Bundle',
          btnFull: 'Complete (72 attachments)',
          thDebit: 'Debit',
          thCredit: 'Credit',
          thAmount: 'Amount',
          thText: 'Posting Text',
          thVoucherId: 'Voucher ID',
          sampleTransitSparkasse: 'Cash in transit Sparkasse → PayPal payout',
          samplePurchaseLego: 'Inventory purchase LEGO bulk lot',
          sampleEbayRevenue: 'Monthly closing eBay sales revenue',
          samplePackaging: 'Packaging & shipping supplies',
          sampleTransitBank: 'Cash in transit Bank → PayPal clearing',
          copiedBtn: '✓ Copied',
          notePrefix: '* Export format: ',
          editionKanzleiDesc: 'Firm Bundle (posting batch + voucher archive)',
          editionFullDesc: 'Complete audit export with 72 attachments',
          noteSuffix: '. Each voucher link opens the original receipt directly in DATEV Unternehmen Online.',
        },
        compliance: {
          kicker: 'GoBD compliant from day one',
          title: 'Compliance without headaches',
          immutableTitle: 'Audit-proof bookkeeping',
          immutableText: 'accountingFox enforces immutable bookings. Cancellations always generate balancing entries — your tax auditor can trace every record seamlessly. Procedural documentation updates automatically.',
          auditTitle: 'Tax audit? No problem.',
          auditText: 'A dedicated auditor view provides tax inspectors with strictly the legally required read-only access — without write permissions and without access to sensitive operational records.',
        },
        faq: [
          { q: 'How does accountingFox reduce effort for tax advisors?', a: 'Your tax advisor receives a prepared DATEV booking batch with linked original receipts. No manual assignment, no follow-up questions about missing documents — the handoff takes minutes instead of hours.' },
          { q: 'Is accountingFox suitable for e-commerce sellers?', a: 'Yes — accountingFox was built specifically for multi-channel sellers operating on eBay, BrickLink, BrickOwl and their own webshops. Automatic reconciliation of all payment streams saves an average of 15+ hours per month.' },
          { q: 'How is legal compliance ensured?', a: 'All bookings are audit-proof and GoBD-compliant. The system enforces immutability — no record is deleted; cancellations always generate reversal entries with checksums.' },
          { q: 'Can my tax auditor access the data?', a: 'Yes, via a dedicated auditor view with read-only access. Your tax auditor sees exactly what is legally required — nothing more and nothing less.' }
        ]
      },
      inventarOwl: {
        badge: 'SaaS Showcase • ERP & Marketplace Intelligence',
        title: 'inventarOwl — Your Inventory. Four Marketplaces. Zero Overselling.',
        lead: 'inventarOwl syncs your inventory in real-time across eBay, BrickLink, BrickOwl, and your own webshop. The integrated marketShark plugin shows you how your prices compare to the market.',
        ctaMarketAnalysis: 'View market price analysis ↓',
        ctaScheduleDemo: 'Schedule a demo ✉',
        mailtoSubject: 'Request inventarOwl demo',
        answerCapsule: {
          answer: 'inventarOwl is the central inventory and catalog system for sellers operating across multiple marketplaces — with integrated market price intelligence through the marketShark plugin.',
          ctaText: 'Market price analysis in detail',
          facts: [
            'One inventory, four marketplaces — no more overselling through real-time synchronization',
            'Integrated marketShark plugin: live market price comparison and profit opportunity analysis',
            'Flexible product structure: sets, individual parts, variations, and bulk lots in one system',
            'Tax-compliant inventory valuation using the weighted average cost method',
            'Seamless handoff to accountingFox for automated bookkeeping'
          ]
        },
        screenshots: {
          heading: 'What to expect',
          inventoryAlt: 'inventarOwl inventory overview — products, stock levels, and channel status',
          inventoryTitle: 'Inventory overview',
          inventoryDesc: 'All products with stock levels, channel status, and pricing at a glance.',
          marketsharkAlt: 'marketShark market price analysis — price comparison and win rates',
          marketsharkTitle: 'marketShark price intelligence',
          marketsharkDesc: 'Live market data for every item: competitor prices, sales history, and your win probability.',
        },
        stats: {
          oversellValue: '0% overselling',
          oversellLabel: 'Real-time inventory synchronization',
          channelsValue: '4 marketplaces',
          channelsLabel: 'eBay · BrickLink · BrickOwl · Shop',
          marketDataValue: 'Live market data',
          marketDataLabel: 'Price comparison via marketShark',
          systemValue: '1 system',
          systemLabel: 'Sets, parts, variants & bulk lots',
        },
        features: {
          sectionTitle: 'What inventarOwl solves for you',
          syncKicker: 'Real-time synchronization',
          syncTitle: 'No more manual stock reconciliation',
          syncText: 'Sell an item on eBay and inventory is instantly deducted across BrickLink, BrickOwl, and your webshop. Orders are reserved upon arrival — not after payment processing.',
          pricingKicker: 'marketShark Plugin',
          pricingTitle: 'Pricing with data instead of gut feeling',
          pricingText: 'For every item, see at a glance: What do competitors charge? What was recently sold? What is your win rate? The scatter plot makes price positioning transparent and data-driven.',
          productKicker: 'Flexible product structure',
          productTitle: 'Sets, parts, and bulk lots in one system',
          productText: 'LEGO merchants sell sets, individual parts, and mixed lots. inventarOwl maps all product types — including bulk parcel breakouts and proportional cost allocations.',
          valuationKicker: 'Weighted average cost method',
          valuationTitle: 'Tax-compliant inventory valuation',
          valuationText: 'Inventory is automatically valued using the weighted average cost method. If market prices drop, the system proposes write-downs — ready for your tax return.',
        },
        marketshark: {
          cardTitle: 'Live Demo: marketShark Market Price Analysis',
          cardKicker: 'How to optimize your selling prices',
          cardText: 'Select a reference item to see how marketShark analyzes market pricing. Red dots show active competitor listings; blue diamonds show actual sales over the last 6 months.',
          selectItem: 'Select item:',
          sampleItemDroid: 'Battle Droid (Single Part)',
          timeTwoDaysAgo: '2 days ago',
          timeToday: 'Today, 14:22',
          timeYesterday: 'Yesterday',
          kpiOfferedAvgTitle: 'Market Average',
          kpiOfferedLotsSub: '{count} active listings',
          kpiSoldAvgTitle: 'Recent Sales (6M)',
          kpiSoldCountSub: '{count} sales',
          kpiOurPriceTitle: 'Your Price',
          kpiVsMarketSub: '{pct}% vs. market',
          kpiWinRateTitle: 'Win Rate',
          kpiWinRateSub: 'Probability of sale',
          legendOffered: 'Competitor listings',
          legendSold: 'Actual sales (6M)',
          legendOurPrice: 'Your price ({price} €)',
          tooltipOffered: 'Offer: {price} €',
          tooltipSold: 'Sale: {price} €',
          svgXAxis: 'Marketplace price distribution',
        },
        channels: {
          cardTitle: 'Multi-Channel Publishing',
          cardKicker: 'One click, four marketplaces',
          cardText: 'Manage where your listings are published from one place. Filter by individual channels to inspect live status:',
          tabAll: 'All Channels',
          thChannel: 'Channel',
          thStatus: 'Status',
          thSync: 'Synchronization',
          thLots: 'Active Listings',
          thLatency: 'Update Latency',
          nameWebshop: 'Own Webshop',
          statusActive: 'Active',
          statusRealtime: 'Real-time',
          modeEbay: 'Automatic variant synchronization',
          modeBricklink: 'Direct real-time inventory reconciliation',
          modeBrickowl: 'Automatic reservation upon incoming order',
          modeWebshop: 'Instant inventory updates',
        },
        faq: [
          { q: 'How does inventarOwl prevent overselling?', a: 'Every sale on one channel instantly reduces available stock across all other channels. The system reserves items at order receipt, not at payment confirmation.' },
          { q: 'What is the advantage of the marketShark plugin?', a: 'You can see at a glance how your price compares to the market. The scatter plot shows current offers and recent sales — so you can optimize prices based on data rather than guesswork.' },
          { q: 'How does tax valuation work?', a: 'inventarOwl automatically calculates weighted average costs and considers the lower of cost or market principle. When market prices decline, the system suggests write-downs automatically.' },
          { q: 'Can I manage bulk lots and mixed inventory?', a: 'Yes — bulk lots and mixed carrier lots are managed as aggregate items. Through a visual carve-out cockpit, you extract individual items that are automatically valued proportionally.' }
        ]
      },
      marketShark: {
        badge: 'SaaS Showcase • Pricing & Arbitrage Intelligence',
        title: 'marketShark — Data-Driven Pricing. Real-Time Arbitrage.',
        lead: 'Real-time market price aggregation across BrickLink, BrickOwl, and eBay. Calculate quantity-weighted medians, filter speculative outliers in milliseconds, and maximize margins with automated arbitrage detection.',
        ctaLiveRadar: 'Test arbitrage radar ↓',
        ctaScheduleDemo: 'Schedule a demo ✉',
        mailtoSubject: 'Request marketShark demo',
        answerCapsule: {
          answer: 'marketShark is the central pricing and arbitrage engine for professional LEGO and toy commerce — featuring live BrickLink OAuth 1.0a integration, outlier elimination, and automated delta repricing.',
          ctaText: 'View arbitrage radar in detail',
          f1: 'Quantity-weighted median across 6-month sales histories instead of distorted arithmetic averages',
          f2: 'Automated outlier filtration (< 20% or > 500% relative to resilient reference price)',
          f3: 'Part-Out Value (POV) decomposition: parting out sets and bulk lots with margin spread calculation',
          f4: 'Win-rate algorithm establishing optimal sell price points with > 80% sell-through probability',
          f5: 'Seamless handoff to inventarOwl for automated delta repricing across all channels',
        },
        screenshotAlt: 'marketShark market price analysis — scatter plot, spreads, and margin opportunities',
        screenshotTitle: 'marketShark Price Scatter & Arbitrage Radar',
        screenshotDesc: 'Live comparison of active stock offers against 6-month real sales with win-rate estimation.',
      },
      osintOwl: {
        badge: 'SaaS Showcase • E-Commerce OSINT & Price Tracking',
        title: 'osintOwl — Autonomous E-Commerce OSINT, Price Tracking & Marketplace Crawler',
        lead: 'Real-time web scraping across BrickLink, eBay, Amazon, and Kleinanzeigen. Rotating residential proxies, automated anti-bot bypass (Turnstile, Akamai, DataDome), and multimodal data extraction for full market transparency.',
        ctaLiveRadar: 'Test scraper radar ↓',
        ctaScheduleDemo: 'Schedule a demo ✉',
        mailtoSubject: 'Request osintOwl demo',
        answerCapsule: {
          answer: 'osintOwl is weretradeIT\'s distributed e-commerce intelligence and web scraping engine — featuring sub-500ms edge scrapers, TLS JA3/JA4 fingerprint spoofing, Camoufox stealth nodes, and direct integration with inventarOwl and marketShark.',
          ctaText: 'View scraper radar in detail',
          f1: 'Distributed edge crawling mesh on Cloudflare Workers with Puppeteer browser rendering bindings',
          f2: 'Anti-bot & captcha resilience: 99.7% bypass success on Cloudflare Turnstile, DataDome, and Akamai',
          f3: 'TLS JA3/JA4 fingerprint spoofing, HTTP/2 stream multiplexing, and canvas/audio entropy masking',
          f4: 'E-commerce schema normalization with Zod: EAN/UPC disambiguation and quantity-weighted median prices',
          f5: 'Real-time webhooks and D1/KV edge storage for instant repricing triggers in inventarOwl and marketShark',
        },
        screenshotAlt: 'osintOwl e-commerce scraper cockpit — live price tracking and anti-bot bypass',
        screenshotTitle: 'osintOwl Scraper Radar & Edge Intelligence',
        screenshotDesc: 'Live comparison of marketplace prices, proxy latencies, and anti-bot bypass rates.',
      },
      infrastructureDog: {
        badge: 'SaaS Showcase • Fleet Observability & Control',
        title: 'infrastructureDog — Full Control Over Your Entire Infrastructure',
        lead: '42 endpoints, automated failover protection, and AI-powered maintenance — infrastructureDog gives you the confidence that your business runs around the clock.',
        ctaLiveDrill: 'Test live DR drill ↓',
        ctaEnterpriseDemo: 'Request enterprise demo ✉',
        mailtoSubject: 'infrastructureDog Enterprise Demo',
        answerCapsule: {
          answer: 'infrastructureDog is the operational control room for all weretradeIT services — real-time monitoring, automated resilience, and AI-powered system management in one dashboard.',
          ctaText: 'Experience disaster recovery drill',
          facts: [
            '42 endpoints across 10 applications — continuously monitored on a 30-second heartbeat',
            'Multi-tier infrastructure: edge network, dedicated servers, and encrypted mesh',
            'Resilient AI pools with automatic provider fallbacks — no single point of failure',
            'Automated disaster recovery drills with verified restoration',
            'Native AI agent interface for autonomous system diagnostics and maintenance'
          ]
        },
        screenshots: {
          heading: 'The cockpit in action',
          tabsAria: 'Cockpit views',
          tabTopology: 'System Topology',
          tabMatrix: 'Status Matrix',
          tabMesh: 'Infrastructure Overview',
          topologyAlt: 'infrastructureDog system topology — all services and connections at a glance',
          topologyDesc: 'Interactive visualization of all services, databases, and connections in production.',
          matrixAlt: 'infrastructureDog status matrix — real-time metrics and availability',
          matrixDesc: 'Latency, availability, and success rates of all endpoints in real time.',
          meshAlt: 'infrastructureDog infrastructure overview — servers, containers, and network',
          meshDesc: 'Dedicated servers, container stacks, and encrypted cross-site network mesh.',
        },
        stats: {
          endpointsValue: '42 endpoints',
          endpointsLabel: 'Continuous monitoring',
          uptimeValue: '99.97% Uptime',
          uptimeLabel: 'Across all services',
          spofValue: 'Zero Single Point',
          spofLabel: 'No single point of failure',
          drValue: 'Auto DR Drills',
          drLabel: 'Verified recovery',
        },
        features: {
          sectionTitle: 'Why enterprises rely on infrastructureDog',
          aiKicker: 'Zero Single Point of Failure',
          aiTitle: 'Resilient AI infrastructure',
          aiText: 'Your AI services run across intelligent model pools rather than single providers. If one provider fails, an alternative takes over automatically — seamless to your users.',
          monitoringKicker: 'Proactive monitoring',
          monitoringTitle: 'Detect problems before they happen',
          monitoringText: '42 endpoints are monitored every 30 seconds. Anomalies are caught and flagged before impacting business operations. No guesswork, no surprises.',
          drKicker: 'Verified disaster recovery',
          drTitle: 'Backups that truly work',
          drText: 'Every company has backups. But do they work in an emergency? infrastructureDog runs regular automated recovery drills — not just on paper.',
          agentsKicker: 'Autonomous system administration',
          agentsTitle: 'AI agents for routine tasks',
          agentsText: 'Authorized AI agents can perform system diagnostics, analyze logs, and handle routine maintenance. Your team stays focused on what matters.',
        },
        fleet: {
          cardTitle: 'Service Overview: 42 Endpoints in Live Status',
          cardKicker: 'Filter by category',
          catAll: 'All (42)',
          catData: 'Data',
          catInfra: 'Infrastructure',
          catAi: 'AI',
          thService: 'Service',
          thDescription: 'Description',
          thStatus: 'Status',
          thLatency: 'Response Time',
          thUptime: 'Availability',
          statusOnline: 'Online',
        },
        drDrill: {
          cardTitle: 'Live Demo: Disaster Recovery Drill',
          cardKicker: 'Experience automated resilience',
          cardText: 'Launch a simulated disaster drill. Within seconds, the system executes a backup restore, an AI provider failover, and a secrets vault check:',
          btnStart: 'Start DR drill ⚡',
          btnRunning: 'Drill in progress...',
          ariaProgress: 'DR drill progress',
          ariaLog: 'DR drill log',
          logReady: "Ready. Click 'Start DR drill' to begin the exercise.",
          log1: '[00:00.000] DR drill started: Automated disaster recovery drill in progress...',
          log2: '[00:00.450] Database snapshot created and encrypted archive stored ✓',
          log3: '[00:01.120] AI provider failover simulated — backup chain active, +18ms latency ✓',
          log4: '[00:01.850] Secrets vault verified, key rotation confirmed ✓',
          log5: '[00:02.340] ✅ DR drill completed: 4/4 system layers validated. Data loss: 0.',
        },
        faq: [
          { q: 'What happens if an AI provider goes down?', a: 'Nothing your users would notice. Our AI models run on intelligent pools with automatic fallback chains. When a provider fails, an alternative takes over within milliseconds.' },
          { q: 'How are backups and disaster recovery ensured?', a: 'Automated nightly database snapshots with encrypted long-term archival. Regular DR drills verify recoverability — not just on paper, but through actual test restores.' },
          { q: 'Can AI agents manage infrastructure independently?', a: 'Yes. Through a native interface, authorized AI agents can run system diagnostics, analyze logs, and handle routine maintenance tasks — without manual SSH sessions.' },
          { q: 'How is availability kept above 99.9%?', a: 'Through multi-level redundancy at every layer: global edge distribution, encrypted multi-site mesh, and automatic failover testing. infrastructureDog monitors itself and reports anomalies before they become problems.' }
        ]
      }
    },
    sisterApps: {
      title: 'Other core apps in the weretradeIT ecosystem:',
      accountingFox: 'accountingFox (GoBD & DATEV)',
      inventarOwl: 'inventarOwl + marketShark Plugin',
      marketShark: 'marketShark (Pricing & Arbitrage Radar)',
      osintOwl: 'osintOwl (OSINT & Scraper Radar)',
      infrastructureDog: 'infrastructureDog (Full Control)',
      plattform: 'Platform Architecture',
    },
  },
};

// Use shared i18n hook with app-specific messages
const { I18nProvider, useI18n: usePackageI18n } = createI18n(messages);

export { I18nProvider };

/**
 * Resolve a dot-separated key against one language's dictionary.
 *
 * This exists because `getNested` inside @weretradeit/hooks ends with
 * `typeof result === 'string' ? result : undefined`, which contradicts the
 * `MessageValue = string | Record<string, any> | any[]` the same module
 * exports. Every array-valued entry therefore resolved to `undefined`, `t()`
 * returned the caller's inline German fallback, and English readers silently
 * got German copy - visible on /bkb/versand and /reinigung, where the stats,
 * process and impact lists never translated.
 *
 * Whatever the path resolves to is returned, which is what `MessageValue`
 * promises. Objects are included on purpose: Plattform.tsx reads whole
 * `{ title, kicker, points }` sections through `t()`, and those sections were
 * rendering an empty bullet list in both languages for the same reason.
 *
 * Returning objects is only safe as long as no caller drops one straight into
 * JSX. Of the 911 `t()` keys used across src/, exactly four resolve to a plain
 * object - platform.coreApps, platform.infrastructureHubs, platform.aiAgentLayer
 * and platform.managedForks - and all four are destructured field by field.
 * A key that points at a namespace and is rendered directly would still crash,
 * so keep requesting leaves.
 */
const lookupMessage = (
  dict: Record<string, MessageValue> | undefined,
  path: string
): MessageValue | undefined => {
  if (!dict) return undefined;
  return path.split('.').reduce<MessageValue | undefined>(
    (acc, part) =>
      acc && typeof acc === 'object' && part in acc
        ? (acc as Record<string, MessageValue>)[part]
        : undefined,
    dict as MessageValue
  );
};

/**
 * Same shape as the package hook, with a `t()` that can also return lists.
 * Language state, persistence and cross-tab sync stay with the package.
 */
export const useI18n = () => {
  const base = usePackageI18n();
  const { lang } = base;

  const t = React.useCallback(
    <T = string,>(key: string, fallback?: T): T => {
      const value = lookupMessage(messages[lang], key);
      return (value as T) ?? (fallback as T) ?? (key as unknown as T);
    },
    [lang]
  );

  return React.useMemo(() => ({ ...base, t }), [base, t]);
};
